import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Pagination,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Icon,
  DatePicker,
  ActionsMenu,
  ActionsMenuItem,
  ActionsMenuDivider,
  showToast,
  Select,
} from '@repo/ui';
import { PageLayout } from '@xtnd/web/shared/layout';
import type { InvoiceQueryParams, TransformedInvoice } from '../types';
import {
  WebhookSimulationModal,
  BulkSimulateModal,
  DownloadMenu,
  BankTransactionsModal,
  ColumnVisibilityModal,
  ProcessingActionBadge,
  BulkJobProgressTracker,
} from '../components';
import { EmailSendConfirmationModal, type BulkTemplateOverrides } from '../components/EmailSendConfirmationModal';
import { WarningsIndicator } from '../components/WarningsIndicator';
import { usePreviewRecipients } from '../hooks/usePreviewRecipients';
import { useJuniperSelection } from '../hooks/useJuniperSelection';
import { TransactionDrawer, type TransactionDrawerActions } from '../components/TransactionDrawer';
import { useTransactionDrawerState } from '../hooks/useTransactionDrawerState';
import { authApi } from '@xtnd/web/features/auth/api/authApi';
import {
  useInvoices,
  useResendEmailMutation,
  useReleaseEmailMutation,
  useBulkSendEmailsMutation,
  useRegeneratePdfMutation,
  useBulkRegeneratePdfMutation,
  useBulkConsolidatePrintMutation,
  useBulkRefreshFromClioMutation,
  useBulkSimulateMutation,
  useBulkVoidDeleteMutation,
  useMarkCompletedMutation,
  useBulkMarkCompletedMutation,
  useTransactionDeliveryDetails,
  useTransactionDeliverySummary,
  useTransactionDocumentHistory,
  useUsersQuery,
} from '../queries';
import { numberUtil, dateUtil, stringUtil } from '@xtnd/web/shared/utils';
import { api } from '@xtnd/web/shared/api';
import { TrustRequestsTransactionsView } from '@xtnd/web/features/trust-requests/components';
import { useEnabledCustomFieldFilters, useCustomFieldFilterOptions } from '../hooks/useCustomFieldFilters';
import type { CustomFieldFilterValue } from '../types';

export function JuniperTransactionsPage() {
  // Control visibility of hidden cards (set to true to show them)
  const showHiddenCards = true;
  const showHiddenSummary = false;

  // Tab state for Evergreen vs Trust Requests
  const [activeTab, setActiveTab] = useState<'evergreen' | 'trust-requests'>('evergreen');

  // Tab-specific column visibility defaults
  const getTabDefaults = useCallback((tab: 'evergreen' | 'trust-requests') => {
    return tab === 'trust-requests' ? {
      // Trust Request defaults - minimal set
      matterType: false,       // Type (hidden)
      clientName: false,       // Client (hidden)
      createdAt: true,         // Created
      updatedAt: false,        // Updated (hidden)
      delivery: true,          // Delivery
      paymentLink: true,       // Payment Link
      operationalBillTotal: false,   // Op Bill Total (hidden)
      operationalBillPaid: false,    // Op Bill Paid (hidden)
      operationalBillBalance: false, // Op Bill Balance (hidden)
      operationalBillDue: false,     // Op Bill Due Amount (hidden)
      operationalBillIssuedAt: false, // Op Bill Issued (hidden)
      operationalBillDueAt: false,   // Op Bill Due (hidden)
      lastOperationalPaymentAt: false, // Op Last Payment (hidden)
      trustRequestBillTotal: true,   // TRR Total
      trustRequestBillPaid: false,   // TRR Paid (hidden)
      trustRequestBillBalance: false, // TRR Balance (hidden)
      trustRequestBillDue: true,     // TRR Due Amount (visible — primary collections column)
      trustRequestBillPending: false, // TRR Pending (hidden)
      trustRequestBillIssuedAt: true, // TRR Issued At
      trustRequestBillDueAt: false, // TRR Due (hidden)
      lastTrustRequestPaymentAt: false, // TRR Last Payment (hidden)
      trustRequestCalculation: false, // Calculation (hidden)
      trustRequestEmailType: false,   // Email Type (hidden)
    } : {
      // Evergreen defaults - comprehensive set
      matterType: true,        // Type
      clientName: false,       // Client (hidden by default)
      createdAt: true,         // Created
      updatedAt: false,        // Updated (hidden by default)
      delivery: true,          // Delivery
      paymentLink: true,       // Payment Link
      operationalBillTotal: true,    // Op Bill Total
      operationalBillPaid: false,    // Op Bill Paid (hidden by default)
      operationalBillBalance: false, // Op Bill Balance (hidden by default)
      operationalBillDue: false,     // Op Bill Due Amount (hidden by default)
      operationalBillIssuedAt: false, // Op Bill Issued (hidden by default)
      operationalBillDueAt: true,    // Op Bill Due
      lastOperationalPaymentAt: false, // Op Last Payment (hidden by default)
      trustRequestBillTotal: true,   // TRR Total
      trustRequestBillPaid: false,   // TRR Paid (hidden by default)
      trustRequestBillBalance: false, // TRR Balance (hidden by default)
      trustRequestBillDue: true,     // TRR Due Amount (visible — primary collections column)
      trustRequestBillPending: false, // TRR Pending (hidden by default)
      trustRequestBillIssuedAt: false, // TRR Issued (hidden by default)
      trustRequestBillDueAt: false, // TRR Due (hidden by default)
      lastTrustRequestPaymentAt: false, // TRR Last Payment (hidden by default)
      trustRequestCalculation: true, // Calculation
      trustRequestEmailType: false,   // Email Type (hidden by default)
    };
  }, []);

  // Load saved column visibility for a tab, merging with defaults for backward compatibility
  const loadColumnsForTab = useCallback((tab: 'evergreen' | 'trust-requests') => {
    const storageKey = `juniper-transactions-columns-${tab}`;
    const saved = localStorage.getItem(storageKey);
    const defaults = getTabDefaults(tab);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Merge: defaults provide new columns, saved values override known columns
        return { ...defaults, ...parsed };
      } catch (e) {
        console.warn('Failed to parse saved column visibility, using defaults');
      }
    }
    // Migrate from old single-key storage on first load
    const legacySaved = localStorage.getItem('juniper-transactions-columns');
    if (legacySaved) {
      try {
        const parsed = JSON.parse(legacySaved);
        return { ...defaults, ...parsed };
      } catch (e) {
        // ignore
      }
    }
    return defaults;
  }, [getTabDefaults]);

  // Column visibility state - persisted per tab in localStorage
  const [visibleColumns, setVisibleColumns] = useState(() => loadColumnsForTab(activeTab));

  // Helper functions for status formatting and date checking
  const formatRawStatus = (state: string | null): string => {
    if (!state) return 'Draft';

    const statusMap: Record<string, string> = {
      not_created: 'Not Created',
      not_required: 'Not Required',
      draft: 'Draft',
      awaiting_approval: 'Awaiting Approval',
      awaiting_payment: 'Awaiting Payment',
      partial_payment: 'Partial Payment',
      pending_payment: 'Pending Payment',
      paid: 'Paid',
      void: 'Void',
      voided: 'Voided',
      deleted: 'Deleted',
      'n/a': 'Not Applicable',
    };

    return statusMap[state] || state;
  };

  // Helper function to format email type for display
  const formatEmailType = (emailType: string | undefined): string => {
    if (!emailType) return 'N/A';

    const emailTypeMap: Record<string, string> = {
      'Trust Establishment': 'Establishment',
      'Mid-Cycle Request': 'Mid-Cycle',
      'Final Bill': 'Final',
    };

    return emailTypeMap[emailType] || emailType;
  };

  const formatDeliveryStatusBucket = (statusBucket?: string): string => {
    switch ((statusBucket || '').toLowerCase()) {
      case 'delivery_notice_received':
        return 'Delivered';
      case 'partial_delivery_notice':
        return 'Partial';
      case 'failed':
        return 'Failed';
      default:
        return 'Pending';
    }
  };

  const getDeliveryStatusBadgeClass = (statusBucket?: string): string => {
    switch ((statusBucket || '').toLowerCase()) {
      case 'delivery_notice_received':
        return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
      case 'partial_delivery_notice':
        return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300';
      case 'failed':
        return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
      default:
        return 'bg-xtnd-dark-50 dark:bg-xtnd-dark-600 text-xtnd-dark-600 dark:text-xtnd-dark-200';
    }
  };

  // Format date for responsive display (date on top, time below on small screens)
  const formatResponsiveDateTime = (
    dateString: string | null | undefined
  ): { date: string; time: string } => {
    if (!dateString) return { date: '', time: '' };

    // Handle invalid date strings
    const date = new Date(dateString);
    if (isNaN(date.getTime()) || dateString === 'Invalid Date') {
      return { date: '', time: '' };
    }

    // Date-only strings (e.g. "2026-03-23") are parsed as UTC midnight.
    // Format them in UTC so the date doesn't shift backwards in local timezone.
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
    const tzOptions = isDateOnly ? { timeZone: 'UTC' as const } : {};

    // Use locale-aware date formatting
    const dateFormatted = date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      ...tzOptions,
    });

    const timeFormatted = isDateOnly ? '' : date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });

    return { date: dateFormatted, time: timeFormatted };
  };

  // Juniper Status badge colors (consistent with Invoice Detail page)
  const getJuniperStatusColor = (status: string): string => {
    const baseClass = 'inline-flex px-2 py-1 text-xs font-semibold rounded-full';

    switch (status.toLowerCase()) {
      case 'completed':
      case 'email sent':
      case 'archived':
        return `${baseClass} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'processing':
      case 'pdf ready':
        return `${baseClass} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
      case 'trust pending approval':
      case 'trust request approved':
      case 'email held':
        return `${baseClass} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'failed':
      case 'trust request deleted':
      case 'trust request voided':
      case 'operational bill voided':
      case 'operational bill deleted':
        return `${baseClass} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      default:
        return `${baseClass} bg-xtnd-dark-50 text-xtnd-dark-700 dark:bg-xtnd-dark-800 dark:text-xtnd-dark-100`;
    }
  };

  // TRR Status badge colors (consistent with Invoice Detail page)
  const getTrustRequestStatusColor = (status: string): string => {
    const baseClass = 'inline-flex px-2 py-1 text-xs font-semibold rounded-full';

    switch (status.toLowerCase()) {
      case 'paid':
      case 'not required':
        return `${baseClass} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'partial payment':
        return `${baseClass} bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-200`;
      case 'pending_payment':
        return `${baseClass} bg-lime-100 text-lime-800 dark:bg-lime-900 dark:text-lime-200`;
      case 'approved':
      case 'sent':
        return `${baseClass} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
      case 'awaiting payment':
        return `${baseClass} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'draft':
      case 'awaiting approval':
        return `${baseClass} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'not created':
        return `${baseClass} bg-xtnd-dark-50 text-xtnd-dark-500 dark:bg-xtnd-dark-700 dark:text-xtnd-dark-200`;
      case 'cancelled':
      case 'void':
      case 'voided':
      case 'deleted':
        return `${baseClass} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      default:
        return `${baseClass} bg-xtnd-dark-50 text-xtnd-dark-700 dark:bg-xtnd-dark-800 dark:text-xtnd-dark-100`;
    }
  };

  // Filter button text colors (matching status badge colors)
  const getFilterButtonTextColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'text-green-900 dark:text-green-900';
      case 'partial payment':
        return 'text-blue-900 dark:text-blue-900';
      case 'pending_payment':
        return 'text-lime-900 dark:text-lime-900';
      case 'awaiting_payment':
        return 'text-yellow-900 dark:text-yellow-900';
      case 'awaiting_approval':
      case 'draft':
        return 'text-yellow-900 dark:text-yellow-900';
      case 'not_created':
        return 'text-xtnd-dark-600 dark:text-xtnd-dark-800';
      case 'void':
      case 'deleted':
        return 'text-red-900 dark:text-red-900';
      default:
        return 'text-xtnd-dark-800 dark:text-xtnd-dark-800';
    }
  };

  // Status count text colors (matching the actual status colors, not section colors)
  const getStatusCountTextColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'paid':
      case 'completed':
      case 'email sent':
      case 'archived':
        return 'text-green-700 dark:text-green-300';
      case 'partial payment':
        return 'text-blue-700 dark:text-blue-300';
      case 'pending_payment':
        return 'text-lime-700 dark:text-lime-300';
      case 'processing':
      case 'pdf ready':
      case 'approved':
      case 'sent':
        return 'text-blue-700 dark:text-blue-300';
      case 'awaiting_payment':
        return 'text-yellow-700 dark:text-yellow-300';
      case 'awaiting_approval':
      case 'draft':
      case 'trust pending approval':
      case 'trust request approved':
      case 'email held':
        return 'text-yellow-700 dark:text-yellow-300';
      case 'not_created':
        return 'text-xtnd-dark-600 dark:text-xtnd-dark-200';
      case 'void':
      case 'deleted':
      case 'failed':
      case 'cancelled':
      case 'voided':
        return 'text-red-700 dark:text-red-300';
      default:
        return 'text-xtnd-dark-600 dark:text-xtnd-dark-200';
    }
  };

  // Helper functions for collapsed filter summaries
  const getSelectedFiltersSummary = (): string => {
    const operationalCount = selectedOperationalStatuses.length;
    const trustRequestCount = selectedTrustRequestStatuses.length;
    const userCount = selectedUserIds.length;
    const juniperCount = selectedJuniperStatuses.length;
    const totalSelected = operationalCount + trustRequestCount + userCount + juniperCount;

    const parts = [];
    if (selectedMatterType !== 'all') parts.push(`type: ${selectedMatterType}`);
    if (selectedMatterId) parts.push(`matter: ${selectedMatterId}`);
    if (billNumberFilter) parts.push(`bill: ${billNumberFilter}`);
    if (pastDueOnly) parts.push('past due');
    if (noPaymentInDays) parts.push(`no payment in ${noPaymentInDays}d`);
    if (operationalCount > 0) parts.push(`${operationalCount} operational`);
    if (trustRequestCount > 0) parts.push(`${trustRequestCount} trust request`);
    if (juniperCount > 0) parts.push(`${juniperCount} juniper`);
    if (userCount > 0) parts.push(`${userCount} user${userCount === 1 ? '' : 's'}`);
    if (selectedSortField !== 'createdAt' || selectedSortDirection !== 'desc') {
      parts.push(`sort: ${selectedSortField} ${selectedSortDirection}`);
    }

    if (parts.length === 0) return 'No filters selected';
    return `${parts.join(', ')} filter${parts.length === 1 && totalSelected === 0 ? '' : 's'} selected`;
  };

  const getSelectedCardsSummary = (): string => {
    if (activeStatusFilters.length === 0) return 'No filters selected';

    const filterNames = activeStatusFilters.map(filter => {
      switch (filter) {
        case 'alert': return 'Alert';
        case 'deletedBills': return 'Deleted & Voided';
        case 'inProcess': return 'In Process';
        case 'operationalPreApproval': return 'Operational Pre Approval';
        case 'trrCreated': return 'Pending TRR Approval';
        case 'pending': return 'Email on Hold';
        case 'sent': return 'Completed';
        default: return filter;
      }
    });

    return `${activeStatusFilters.length} filter${activeStatusFilters.length !== 1 ? 's' : ''} active: ${filterNames.join(', ')}`;
  };

  const getDateRangeSummary = (): string => {
    if (!startDate || !endDate) return 'All dates';
    const fieldLabel = dateField === 'issuedAt' ? 'Issued' : 'Created';
    return `${fieldLabel}: ${dateUtil.formatDate(new Date(startDate), dateUtil.DateFormat.US)} - ${dateUtil.formatDate(new Date(endDate), dateUtil.DateFormat.US)}`;
  };

  // Helper function to check if transaction has valid recipients
  const hasValidRecipients = (invoice: TransformedInvoice): boolean => {
    if (!invoice.billRecipientEmails || invoice.billRecipientEmails.length === 0) {
      return false;
    }

    // Handle both formats: array of strings or array of objects
    const validRecipients = invoice.billRecipientEmails.filter(recipient => {
      // If recipient is a string, it's a valid email address
      if (typeof recipient === 'string') {
        return recipient && (recipient as string).trim() !== '';
      }

      // If recipient is an object, check for is_bill_recipient=true AND has non-empty primary_email_address
      if (typeof recipient === 'object' && recipient !== null) {
        return (
          recipient.is_bill_recipient &&
          recipient.primary_email_address &&
          recipient.primary_email_address.trim() !== ''
        );
      }

      return false;
    });

    return validRecipients.length > 0;
  };

  // Convert a transaction-level exception into a concise, action-oriented
  // warning string for the warning column + hover tooltip. Keep these short —
  // they render in a cramped tooltip alongside other bullet points.
  const formatExceptionMessage = (ex: NonNullable<TransformedInvoice['exceptions']>[number]): string => {
    switch (ex.type) {
      case 'evergreen_missing_custom_field_bank_account':
        return 'Evergreen matter custom-field bank account is empty — set it on the matter in Clio, then Regenerate PDF';
      case 'custom_field_bank_account_type_mismatch': {
        const details = ex as unknown as {
          matterType?: string;
          requiredAccountType?: string;
          actualAccountType?: string;
        };
        const matterType = details.matterType ?? 'unknown';
        const actual = details.actualAccountType ?? 'wrong type';
        const required = details.requiredAccountType ?? 'correct type';
        return `Matter is ${matterType}; custom-field points at a ${actual} account (needs ${required}). Fix the custom-field value, clear it, or reclassify the matter`;
      }
      case 'missing_destination_account':
        return 'No destination bank account configured for this bill';
      case 'missing_billing_recipients':
        return 'Email delivery setup incomplete: no billing recipients configured';
      case 'configuration_not_found':
        return 'Juniper configuration not found for tenant';
      default:
        return ex.message || `Unresolved issue: ${ex.type}`;
    }
  };

  // Helper function to get warning messages for a transaction
  const getTransactionWarnings = (invoice: TransformedInvoice): string[] => {
    const warnings: string[] = [];

    // Check for missing recipient emails
    if (!hasValidRecipients(invoice)) {
      if (!invoice.billRecipientEmails || invoice.billRecipientEmails.length === 0) {
        warnings.push('No bill recipients found');
      } else {
        warnings.push('No valid email addresses for bill recipients');
      }
    }

    // Check for operational bill voided but trust request not deleted/voided
    const operationalBillStatus = invoice.operationalBill?.state?.toLowerCase() || '';
    const trustRequestBillStatus = invoice.trustRequest?.state?.toLowerCase() || '';
    if (
      operationalBillStatus === 'void' &&
      trustRequestBillStatus !== 'deleted' &&
      trustRequestBillStatus !== 'void' &&
      trustRequestBillStatus !== 'voided'
    ) {
      warnings.push('The Deleting or Voiding the trust request should be evaluated');
    }

    // Unresolved transaction-level exceptions (master-data errors, missing
    // configuration, etc.) — these block PDF generation and require an admin
    // to fix the matter or tenant configuration in Clio before Regenerate PDF
    // will succeed.
    (invoice.exceptions ?? [])
      .filter(ex => ex && ex.resolved !== true && ex.severity !== 'info')
      .forEach(ex => {
        warnings.push(formatExceptionMessage(ex));
      });

    return warnings;
  };

  // Email eligibility helper functions
  // Check if invoice is eligible for resend/release.
  // - Trust Request flows: awaiting_payment or paid are eligible; when `not_required` only allow if status is Email Held.
  // - Evergreen / Operational flows: Email Held should be eligible for send.
  // - Any transaction with juniperStatus `completed` is eligible for Resend.
  const isResendEligible = (invoice: TransformedInvoice): boolean => {
    const trustRequestState = invoice.trustRequest?.state?.toLowerCase();
    const juniperStatus = invoice.juniperStatus?.toLowerCase();
    const matterType = invoice.matterType;

    const isEmailHeld = juniperStatus === 'email held';
    const isCompleted = juniperStatus === 'completed';

    // Completed transactions are always eligible for "Resend"
    if (isCompleted) return true;

    // Email Held should be allowed for Operational and Evergreen transactions
    if (isEmailHeld && (matterType === 'Operational' || matterType === 'Evergreen')) return true;

    // Trust Request specific rules
    if (trustRequestState === 'awaiting_payment' || trustRequestState === 'paid') return true;

    // Allow Email Held only for trust requests in `not_required` state (legacy behavior)
    if (trustRequestState === 'not_required' && isEmailHeld) return true;

    return false;
  };


  // Check if invoice is eligible for PDF regeneration. Must stay in sync with
  // RegenPreflightService.isAllowedForAction('regen') in apps/api.
  // - Evergreen (Trust Request) transactions: must be in awaiting_payment or
  //   partial_payment state (partial_payment is the JT-side label for a Clio
  //   bill in awaiting_payment with paid > 0; both flow into regen).
  // - Operational transactions: must be in Email Held or Completed status,
  //   and operational bill must not be paid, voided, or deleted
  const isPdfRegenerationEligible = (invoice: TransformedInvoice): boolean => {
    const matterType = invoice.matterType;
    const trustRequestState = invoice.trustRequest?.state?.toLowerCase();
    const juniperStatus = invoice.juniperStatus?.toLowerCase();
    const operationalBillState = invoice.operationalBill?.state?.toLowerCase();

    if (matterType === 'Evergreen') {
      return trustRequestState === 'awaiting_payment' || trustRequestState === 'partial_payment';
    }

    if (matterType === 'Operational') {
      const statusOk = juniperStatus === 'email held' || juniperStatus === 'completed';
      const billBlocked =
        operationalBillState === 'paid' ||
        operationalBillState === 'void' ||
        operationalBillState === 'voided' ||
        operationalBillState === 'deleted';
      return statusOk && !billBlocked;
    }

    return false;
  };

  const isConsolidatePrintEligible = (invoice: TransformedInvoice): boolean => {
    const juniperStatus = invoice.juniperStatus?.toLowerCase();
    const s3Key = invoice.documents?.storedAssets?.pdfs?.invoice?.s3Key;
    return juniperStatus === 'email held' && !!s3Key;
  };

  // Check if invoice is eligible for void/delete (Evergreen trust requests in draft, awaiting_approval, or awaiting_payment)
  const isVoidDeleteEligible = (invoice: TransformedInvoice): boolean => {
    if (invoice.matterType !== 'Evergreen') return false;
    const state = invoice.trustRequest?.state?.toLowerCase();
    return state === 'draft' || state === 'awaiting_approval' || state === 'awaiting_payment';
  };

  const isMarkCompletedEligible = (invoice: TransformedInvoice): boolean =>
    invoice.juniperStatus?.toLowerCase() === 'email held';

  const getVoidDeleteAction = (invoice: TransformedInvoice): 'Delete' | 'Void' | null => {
    const state = invoice.trustRequest?.state?.toLowerCase();
    if (state === 'draft' || state === 'awaiting_approval') return 'Delete';
    if (state === 'awaiting_payment') return 'Void';
    return null;
  };

  // Helper function to get default date range (current week)
  const getDefaultDateRange = () => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay()); // Get Sunday of current week
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + (6 - today.getDay())); // Get Saturday of current week

    return {
      startDate: startOfWeek.toISOString().split('T')[0],
      endDate: endOfWeek.toISOString().split('T')[0],
    };
  };

  // URL search params for date persistence
  const [searchParams, setSearchParams] = useSearchParams();

  // Date range state for the date picker - initialized from URL params or defaults
  const [startDate, setStartDate] = useState(() => {
    const urlStartDate = searchParams.get('startDate');
    const urlEndDate = searchParams.get('endDate');

    if (urlStartDate && urlEndDate) {
      // Validate that the dates are reasonable (not too far in the past or future)
      const start = new Date(urlStartDate);
      const end = new Date(urlEndDate);
      const now = new Date();

      // Check if dates are within a reasonable range (last 2 years to next year)
      const twoYearsAgo = new Date(now.getFullYear() - 2, 0, 1);
      const nextYear = new Date(now.getFullYear() + 1, 11, 31);

      if (start >= twoYearsAgo && end <= nextYear && start <= end) {
        return urlStartDate;
      }
    }

    // Fall back to default date range
    return getDefaultDateRange().startDate;
  });

  const [endDate, setEndDate] = useState(() => {
    const urlStartDate = searchParams.get('startDate');
    const urlEndDate = searchParams.get('endDate');

    if (urlStartDate && urlEndDate) {
      // Validate that the dates are reasonable (not too far in the past or future)
      const start = new Date(urlStartDate);
      const end = new Date(urlEndDate);
      const now = new Date();

      // Check if dates are within a reasonable range (last 2 years to next year)
      const twoYearsAgo = new Date(now.getFullYear() - 2, 0, 1);
      const nextYear = new Date(now.getFullYear() + 1, 11, 31);

      if (start >= twoYearsAgo && end <= nextYear && start <= end) {
        return urlEndDate;
      }
    }

    // Fall back to default date range
    return getDefaultDateRange().endDate;
  });

  // Wrapper function to update both state and URL parameters
  const updateDateRange = (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('startDate', newStartDate);
    newSearchParams.set('endDate', newEndDate);
    setSearchParams(newSearchParams);
  };

  // Which date field the date range filter applies to ('createdAt' | 'issuedAt').
  // 'issuedAt' filters on operationalBill.issued_at.
  const [dateField, setDateField] = useState<'createdAt' | 'issuedAt'>(() => {
    const v = searchParams.get('dateField');
    return v === 'issuedAt' ? 'issuedAt' : 'createdAt';
  });

  const updateDateField = (newField: 'createdAt' | 'issuedAt') => {
    setDateField(newField);
    const newSearchParams = new URLSearchParams(searchParams);
    if (newField === 'createdAt') {
      newSearchParams.delete('dateField');
    } else {
      newSearchParams.set('dateField', newField);
    }
    setSearchParams(newSearchParams);
  };

  // Filter state - Multi-select array with default 4 filters pre-selected
  const DEFAULT_ACTIVE_FILTERS = ['inProcess', 'operationalPreApproval', 'trrCreated', 'pending', 'sent'];
  
  const [activeStatusFilters, setActiveStatusFilters] = useState<string[]>(() => {
    // Check URL params first, fallback to defaults
    const urlFilters = searchParams.get('statusFilters');
    if (urlFilters) {
      const parsedFilters = urlFilters.split(',').filter(Boolean);
      return parsedFilters.length > 0 ? parsedFilters : DEFAULT_ACTIVE_FILTERS;
    }
    return DEFAULT_ACTIVE_FILTERS;
  });
  
  const [isWebhookModalOpen, setIsWebhookModalOpen] = useState(false);

  // Completed sub-filter state - initialized from URL params
  const [selectedOperationalStatuses, setSelectedOperationalStatuses] = useState<string[]>(() => {
    const urlParams = searchParams.get('operationalStatuses');
    return urlParams ? urlParams.split(',').filter(Boolean) : [];
  });
  const [selectedTrustRequestStatuses, setSelectedTrustRequestStatuses] = useState<string[]>(() => {
    const urlParams = searchParams.get('trustRequestStatuses');
    return urlParams ? urlParams.split(',').filter(Boolean) : [];
  });

  // User filter state
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(() => {
    const urlParams = searchParams.get('userIds');
    return urlParams ? urlParams.split(',').filter(Boolean) : [];
  });

  // Matter type filter state (Evergreen tab only: 'all' | 'Evergreen' | 'Operational')
  const [selectedMatterType, setSelectedMatterType] = useState<'all' | 'Evergreen' | 'Operational'>(() => {
    const urlParam = searchParams.get('matterType');
    return (urlParam === 'Evergreen' || urlParam === 'Operational') ? urlParam : 'all';
  });
  const [selectedMatterId, setSelectedMatterId] = useState(() => searchParams.get('matterId') || '');
  const [matterOptions, setMatterOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [matterSearchLoading, setMatterSearchLoading] = useState(false);
  const matterSearchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [billNumberFilter, setBillNumberFilter] = useState(() => searchParams.get('billNumber') || '');
  const [pastDueOnly, setPastDueOnly] = useState(() => searchParams.get('pastDue') === 'true');
  const [noPaymentInDays, setNoPaymentInDays] = useState<number | undefined>(() => {
    const urlParam = searchParams.get('noPaymentInDays');
    return urlParam ? parseInt(urlParam, 10) : undefined;
  });
  const [selectedJuniperStatuses, setSelectedJuniperStatuses] = useState<string[]>(() => {
    const urlParams = searchParams.get('juniperStatuses');
    return urlParams ? urlParams.split(',').filter(Boolean) : [];
  });

  // Custom field filter state - initialized from URL params (cf_<fieldId>=value1,value2)
  const [customFieldSelections, setCustomFieldSelections] = useState<Record<number, string[]>>(() => {
    const selections: Record<number, string[]> = {};
    searchParams.forEach((value, key) => {
      if (key.startsWith('cf_')) {
        const fieldId = parseInt(key.slice(3), 10);
        if (!isNaN(fieldId)) {
          selections[fieldId] = value.split(',').filter(Boolean);
        }
      }
    });
    return selections;
  });

  const [selectedSortField, setSelectedSortField] = useState<
    'createdAt' | 'lastSentAt' | 'operationalBillBalance' | 'trustRequestBalance' | 'lastOperationalPaymentAt' | 'lastTrustRequestPaymentAt'
  >(() => {
    const urlParam = searchParams.get('sortField');
    if (
      urlParam === 'lastSentAt' ||
      urlParam === 'operationalBillBalance' ||
      urlParam === 'trustRequestBalance' ||
      urlParam === 'lastOperationalPaymentAt' ||
      urlParam === 'lastTrustRequestPaymentAt'
    ) {
      return urlParam;
    }
    return 'createdAt';
  });
  const [selectedSortDirection, setSelectedSortDirection] = useState<'asc' | 'desc'>(() => {
    const urlParam = searchParams.get('sortDirection');
    return urlParam === 'asc' ? 'asc' : 'desc';
  });

  // Pending (staged) state for Matter / Sort card — only applied on "Apply" click
  const [pendingMatterId, setPendingMatterId] = useState(selectedMatterId);
  const [pendingBillNumber, setPendingBillNumber] = useState(billNumberFilter);
  const [pendingSortField, setPendingSortField] = useState(selectedSortField);
  const [pendingSortDirection, setPendingSortDirection] = useState(selectedSortDirection);
  const [pendingPastDue, setPendingPastDue] = useState(pastDueOnly);
  const [pendingNoPaymentInDays, setPendingNoPaymentInDays] = useState(noPaymentInDays);

  const hasPendingMatterSortChanges =
    pendingMatterId !== selectedMatterId ||
    pendingBillNumber !== billNumberFilter ||
    pendingSortField !== selectedSortField ||
    pendingSortDirection !== selectedSortDirection ||
    pendingPastDue !== pastDueOnly ||
    pendingNoPaymentInDays !== noPaymentInDays;

  const applyMatterSortFilters = () => {
    setSelectedMatterId(pendingMatterId);
    setBillNumberFilter(pendingBillNumber);
    setSelectedSortField(pendingSortField);
    setSelectedSortDirection(pendingSortDirection);
    setPastDueOnly(pendingPastDue);
    setNoPaymentInDays(pendingNoPaymentInDays);

    const next = new URLSearchParams(searchParams);
    // Matter
    if (pendingMatterId) { next.set('matterId', pendingMatterId); } else { next.delete('matterId'); }
    // Bill number
    if (pendingBillNumber.trim()) { next.set('billNumber', pendingBillNumber.trim()); } else { next.delete('billNumber'); }
    // Sort
    next.set('sortField', pendingSortField);
    next.set('sortDirection', pendingSortDirection);
    // Past due
    if (pendingPastDue) { next.set('pastDue', 'true'); } else { next.delete('pastDue'); }
    // No payment in days
    if (pendingNoPaymentInDays && pendingNoPaymentInDays > 0) {
      next.set('noPaymentInDays', pendingNoPaymentInDays.toString());
    } else {
      next.delete('noPaymentInDays');
    }
    setSearchParams(next);
    resetPagination();
  };

  // Collapsible state for filter sections
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);
  const [isSummaryCollapsed, setIsSummaryCollapsed] = useState(false);
  const [isCardsCollapsed, setIsCardsCollapsed] = useState(false); // Start expanded

  // Pagination state - cursor-based pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const allowedSizes = [25, 50, 100, 200];
    const defaultSize = 200;
    const saved = sessionStorage.getItem('juniper-transactions-page-size');
    if (saved) {
      try {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed) && allowedSizes.includes(parsed)) return parsed;
      } catch (e) {
        console.warn('Failed to parse saved page size, using default');
      }
    }
    return defaultSize;
  });
  const [currentCursor, setCurrentCursor] = useState<string | undefined>(undefined);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);

  // Matter search handler for searchable dropdown
  const handleMatterSearch = useCallback((inputValue: string) => {
    if (matterSearchTimerRef.current) clearTimeout(matterSearchTimerRef.current);
    if (!inputValue || inputValue.length < 2) {
      setMatterOptions([]);
      return;
    }
    setMatterSearchLoading(true);
    matterSearchTimerRef.current = setTimeout(async () => {
      try {
        const response = await api.get<{ data: Array<{ id: string; displayNumber: string; clientName: string }> }>(
          `/juniper-query/matters/for-filtering?search=${encodeURIComponent(inputValue)}`
        );
        setMatterOptions(
          (response.data || []).map(m => ({
            value: String(m.id),
            label: `${m.displayNumber}${m.clientName ? ` - ${m.clientName}` : ''}`,
          }))
        );
      } catch {
        setMatterOptions([]);
      } finally {
        setMatterSearchLoading(false);
      }
    }, 300);
  }, []);

  // Resolve matter display label when loading from URL params.
  // Direct GET by matterId — avoids a full-tenant scan of the filtering endpoint
  // for tenants with large matter lists.
  useEffect(() => {
    if (selectedMatterId && matterOptions.length === 0) {
      api.get<{ id: number | string; display_number?: string; client?: { name?: string } }>(
        `/juniper-query/matters/${encodeURIComponent(selectedMatterId)}?fields=id,display_number,client.name`
      ).then(matter => {
        if (matter && String(matter.id) === selectedMatterId) {
          const displayNumber = matter.display_number || '';
          const clientName = matter.client?.name || '';
          setMatterOptions([{
            value: String(matter.id),
            label: `${displayNumber}${clientName ? ` - ${clientName}` : ''}`,
          }]);
        }
      }).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  // Bulk selection state — tagged-union selection that survives pagination and
  // supports "select all matching filters" across every page.
  const selection = useJuniperSelection(totalRecords);

  // One-time teaching hint for the "Select all N across all pages" flow.
  // We persist dismissal in localStorage so returning users don't see it again.
  const SELECT_ALL_HINT_STORAGE_KEY = 'juniper-transactions-selectall-hint-seen';
  const [selectAllHintDismissed, setSelectAllHintDismissed] = useState(() => {
    try {
      return !!localStorage.getItem(SELECT_ALL_HINT_STORAGE_KEY);
    } catch {
      return true;
    }
  });
  const dismissSelectAllHint = useCallback(() => {
    setSelectAllHintDismissed(true);
    try {
      localStorage.setItem(SELECT_ALL_HINT_STORAGE_KEY, '1');
    } catch {
      // ignore storage errors — dismissal still applies to this session
    }
  }, []);

  // Save column visibility to localStorage whenever it changes (per-tab key)
  React.useEffect(() => {
    localStorage.setItem(`juniper-transactions-columns-${activeTab}`, JSON.stringify(visibleColumns));
  }, [visibleColumns, activeTab]);

  // Load saved column visibility when tab changes (preserves user preferences per tab)
  React.useEffect(() => {
    setVisibleColumns(loadColumnsForTab(activeTab));
    // Reset matter type filter when switching tabs
    setSelectedMatterType('all');
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('matterType');
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // intentionally exclude setSearchParams - it's recreated on every URL change in react-router, which would cause this effect to run on every filter click

  // Save page size to sessionStorage whenever it changes
  React.useEffect(() => {
    sessionStorage.setItem('juniper-transactions-page-size', pageSize.toString());
  }, [pageSize]);

  // Column visibility modal state
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);

  // Action menu states
  const [downloadMenuState, setDownloadMenuState] = useState<{
    isOpen: boolean;
    position: { x: number; y: number };
    transaction: any;
  }>({ isOpen: false, position: { x: 0, y: 0 }, transaction: null });
  const [reprocessing, setReprocessing] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<any | null>(null);
  const previewRecipients = usePreviewRecipients();

  // PDF regeneration confirmation state
  const [regenerateConfirmOpen, setRegenerateConfirmOpen] = useState(false);
  const [regenerateConfirmType, setRegenerateConfirmType] = useState<'single' | 'bulk'>('single');
  const [regenerateConfirmTarget, setRegenerateConfirmTarget] = useState<any | null>(null);
  const [regenerateConfirmBulkIds, setRegenerateConfirmBulkIds] = useState<string[]>([]);
  const [actionsMenuOpen, setActionsMenuOpen] = useState<string | null>(null); // Track which row's menu is open
  const [menuTriggerElement, setMenuTriggerElement] = useState<HTMLElement | null>(null); // Track trigger element for menu positioning
  const [bankTransactionsModalOpen, setBankTransactionsModalOpen] = useState(false);
  const [selectedTransactionForBankModal, setSelectedTransactionForBankModal] =
    useState<TransformedInvoice | null>(null);
  const [bulkSimulateModalOpen, setBulkSimulateModalOpen] = useState(false);
  const [bulkActionsMenuOpen, setBulkActionsMenuOpen] = useState(false);
  const bulkActionsButtonRef = React.useRef<HTMLButtonElement>(null);
  const [bulkEligibleIds, setBulkEligibleIds] = useState<string[]>([]);
  const [bulkEligibleByType, setBulkEligibleByType] = useState<Record<string, string[]>>({});
  const [bulkHeldCountByType, setBulkHeldCountByType] = useState<Record<string, number>>({});

  // Tenant ID for drawer tab visibility
  const currentUser = authApi.getCurrentUser();
  const tenantId = currentUser?.tenantId;

  // Void/Delete confirmation state
  const [voidDeleteConfirmOpen, setVoidDeleteConfirmOpen] = useState(false);
  const [voidDeleteTarget, setVoidDeleteTarget] = useState<TransformedInvoice | null>(null);
  const [voidDeleteIsBulk, setVoidDeleteIsBulk] = useState(false);

  // Reset pagination when filters change
  const resetPagination = useCallback(() => {
    setCurrentPage(1);
    setCurrentCursor(undefined);
    setHasNextPage(false);
    setHasPreviousPage(false);
    setTotalRecords(0);
    setCursorHistory([]);
  }, []); // State setters are stable and don't need to be in dependencies

  // Helper function to update URL params for sub-filters
  const updateSubFilterUrlParams = (
    operationalStatuses: string[],
    trustRequestStatuses: string[],
    userIds?: string[]
  ) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (operationalStatuses.length > 0) {
      newSearchParams.set('operationalStatuses', operationalStatuses.join(','));
    } else {
      newSearchParams.delete('operationalStatuses');
    }
    if (trustRequestStatuses.length > 0) {
      newSearchParams.set('trustRequestStatuses', trustRequestStatuses.join(','));
    } else {
      newSearchParams.delete('trustRequestStatuses');
    }
    if (userIds && userIds.length > 0) {
      newSearchParams.set('userIds', userIds.join(','));
    } else {
      newSearchParams.delete('userIds');
    }
    setSearchParams(newSearchParams);
  };

  // Handle status card clicks for filtering - Toggle behavior for multi-select
  const handleStatusCardClick = (filterType: string) => {
    console.log('🎯 Status card clicked:', filterType);
    setActiveStatusFilters(current => {
      const isCurrentlyActive = current.includes(filterType);
      
      let newFilters: string[];
      if (isCurrentlyActive) {
        // Remove filter - could result in no filters (show nothing)
        newFilters = current.filter(f => f !== filterType);
      } else {
        // Add filter
        newFilters = [...current, filterType];
      }
      
      // Update URL params
      const newSearchParams = new URLSearchParams(searchParams);
      if (newFilters.length > 0) {
        newSearchParams.set('statusFilters', newFilters.join(','));
      } else {
        newSearchParams.delete('statusFilters');
      }
      setSearchParams(newSearchParams);
      
      return newFilters;
    });
    
    // Clear sub-filters when changing card selection
    setSelectedOperationalStatuses([]);
    setSelectedTrustRequestStatuses([]);
    setSelectedUserIds([]);
    updateSubFilterUrlParams([], [], []);
    resetPagination();
  };

  // Handle sub-filter selection for operational status
  const handleOperationalStatusToggle = (status: string) => {
    const newStatuses = selectedOperationalStatuses.includes(status)
      ? selectedOperationalStatuses.filter(s => s !== status)
      : [...selectedOperationalStatuses, status];
    setSelectedOperationalStatuses(newStatuses);
    updateSubFilterUrlParams(newStatuses, selectedTrustRequestStatuses);
    resetPagination();
  };

  // Handle sub-filter selection for trust request status
  const handleTrustRequestStatusToggle = (status: string) => {
    const newStatuses = selectedTrustRequestStatuses.includes(status)
      ? selectedTrustRequestStatuses.filter(s => s !== status)
      : [...selectedTrustRequestStatuses, status];
    setSelectedTrustRequestStatuses(newStatuses);
    updateSubFilterUrlParams(selectedOperationalStatuses, newStatuses, selectedUserIds);
    resetPagination();
  };

  // Handle user filter selection
  const handleUserToggle = (userId: string) => {
    const newUserIds = selectedUserIds.includes(userId)
      ? selectedUserIds.filter(id => id !== userId)
      : [...selectedUserIds, userId];
    setSelectedUserIds(newUserIds);
    updateSubFilterUrlParams(selectedOperationalStatuses, selectedTrustRequestStatuses, newUserIds);
    resetPagination();
  };

  // Get display name for filter type - helper function for filter names
  // (Removed as it's no longer needed with multi-select implementation)

  // Update URL param for matter type filter
  const updateMatterTypeParam = (value: 'all' | 'Evergreen' | 'Operational') => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (value === 'all') {
      newSearchParams.delete('matterType');
    } else {
      newSearchParams.set('matterType', value);
    }
    setSearchParams(newSearchParams);
  };

  // Build query parameters for API
  const queryParams = useMemo((): InvoiceQueryParams => {
    const params: InvoiceQueryParams = {
      limit: pageSize,
      dateFrom: startDate,
      dateTo: endDate,
      dateField,
      uniqueByMatter: false, // CRITICAL: Show ALL transactions, not just unique by matter
      createdByUserIds: selectedUserIds.length > 0 ? selectedUserIds : undefined,
      matterId: selectedMatterId || undefined,
      matterType: selectedMatterType,
      mainBuckets: activeStatusFilters,
      operationalStates: selectedOperationalStatuses.length > 0 ? selectedOperationalStatuses : undefined,
      trustRequestStates: selectedTrustRequestStatuses.length > 0 ? selectedTrustRequestStatuses : undefined,
      juniperStatuses: selectedJuniperStatuses.length > 0 ? selectedJuniperStatuses : undefined,
      pastDue: pastDueOnly || undefined,
      noPaymentInDays: noPaymentInDays || undefined,
      billNumber: billNumberFilter || undefined,
      sortBy: selectedSortField,
      sortDirection: selectedSortDirection,
      customFieldFilters: (() => {
        const filters: CustomFieldFilterValue[] = [];
        for (const [fieldId, values] of Object.entries(customFieldSelections)) {
          if (values.length > 0) {
            filters.push({ fieldId: Number(fieldId), values });
          }
        }
        return filters.length > 0 ? filters : undefined;
      })(),
    };

    if (currentCursor) {
      params.cursor = currentCursor;
    }

    console.log('🔧 Juniper Transactions Query Params:', {
      limit: params.limit,
      pageSize: pageSize,
      cursor: params.cursor,
      page: currentPage,
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
      uniqueByMatter: params.uniqueByMatter,
      matterId: params.matterId,
      mainBuckets: params.mainBuckets,
      operationalStates: params.operationalStates,
      trustRequestStates: params.trustRequestStates,
      juniperStatuses: params.juniperStatuses,
      pastDue: params.pastDue,
      sortBy: params.sortBy,
      sortDirection: params.sortDirection,
    });

    return params;
  }, [
    currentCursor,
    pageSize,
    currentPage,
    startDate,
    endDate,
    dateField,
    activeStatusFilters,
    selectedUserIds,
    selectedMatterId,
    selectedMatterType,
    selectedOperationalStatuses,
    selectedTrustRequestStatuses,
    selectedJuniperStatuses,
    pastDueOnly,
    billNumberFilter,
    selectedSortField,
    selectedSortDirection,
    customFieldSelections,
  ]);

  // Reset "select all matching" mode whenever the filters change so the user
  // never actions records outside the filter set they saw at selection time.
  const onFiltersChanged = selection.onFiltersChanged;
  useEffect(() => {
    onFiltersChanged(queryParams);
  }, [queryParams, onFiltersChanged]);

  // Fetch invoices and server-side summary in a single hook
  const {
    data: invoicesResponse,
    isLoading: isLoadingInvoices,
    isFetching: isFetchingInvoices,
    error: invoicesError,
    refetch: refetchInvoices,
  } = useInvoices(queryParams);

  // Fetch user names for filtering (filtered to only users with transactions)
  const {
    data: userNames,
    isLoading: isLoadingUsers,
    error: usersError,
  } = useUsersQuery();

  // Custom field filters - fetch enabled config and picklist options
  const { data: enabledCustomFieldFilters = [] } = useEnabledCustomFieldFilters();
  const { data: customFieldFilterOptions = [] } = useCustomFieldFilterOptions(enabledCustomFieldFilters);

  // Pagination handlers - cursor-based (defined AFTER invoicesResponse)
  const handleNextPage = useCallback(() => {
    if (hasNextPage && invoicesResponse?.nextCursor) {
      const cursorToStore = currentCursor || 'FIRST_PAGE';
      setCursorHistory(prev => [...prev, cursorToStore]);

      setCurrentPage(prev => prev + 1);
      setCurrentCursor(invoicesResponse.nextCursor);
      setHasPreviousPage(true);

      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [hasNextPage, invoicesResponse?.nextCursor, currentCursor]); // State setters are stable

  const handlePreviousPage = useCallback(() => {
    if (hasPreviousPage && cursorHistory.length > 0) {
      const newHistory = [...cursorHistory];
      const previousCursor = newHistory.pop();

      setCursorHistory(newHistory);
      setCurrentPage(prev => prev - 1);

      if (previousCursor === 'FIRST_PAGE') {
        setCurrentCursor(undefined);
      } else {
        setCurrentCursor(previousCursor);
      }

      setHasPreviousPage(newHistory.length > 0);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [hasPreviousPage, cursorHistory]); // State setters are stable

  const handlePageChange = useCallback((page: number) => {
    if (page > currentPage) {
      handleNextPage();
    } else if (page < currentPage) {
      handlePreviousPage();
    }
  }, [currentPage, handleNextPage, handlePreviousPage]);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    resetPagination();
  }, [resetPagination]); // setPageSize is stable

  // Extract invoices from response
  const invoices = useMemo(
    () => (Array.isArray(invoicesResponse?.data) ? invoicesResponse.data : []),
    [invoicesResponse?.data]
  );

  // Debug logging
  React.useEffect(() => {
    console.log('📊 Juniper Transactions Debug:', {
      totalInvoices: invoices.length,
      activeStatusFilters,
      dateRange: { startDate, endDate },
      pageSize,
      hasActiveFilter: activeStatusFilters.length > 0,
    });
  }, [invoices.length, activeStatusFilters, startDate, endDate, pageSize]);

  const counts = invoicesResponse?.summary?.bucketCounts || {
    pending: 0,
    alert: 0,
    trrCreated: 0,
    sent: 0,
    deletedBills: 0,
    inProcess: 0,
    pastDue: 0,
    operationalPreApproval: 0,
  };
  const isLoadingCounts = isLoadingInvoices;

  // Update pagination state from response
  React.useEffect(() => {
    if (invoicesResponse) {
      console.log('📊 Invoice Response Debug:', {
        dataLength: invoicesResponse.data?.length || 0,
        hasMore: invoicesResponse.hasMore,
        nextCursor: invoicesResponse.nextCursor,
        metadata: invoicesResponse.metadata,
        totalFetched: invoicesResponse.metadata?.totalFetched,
        limit: invoicesResponse.limit,
        currentPage,
        cursorHistoryLength: cursorHistory.length,
      });

      // Update hasNextPage based on API response
      const hasMore = Boolean(invoicesResponse.hasMore && invoicesResponse.nextCursor);
      console.log('🔄 Setting hasNextPage to:', hasMore, {
        hasMoreFromAPI: invoicesResponse.hasMore,
        hasNextCursor: !!invoicesResponse.nextCursor,
        nextCursorValue: invoicesResponse.nextCursor,
      });
      setHasNextPage(hasMore);
      setTotalRecords(invoicesResponse.pageInfo?.totalMatching || 0);
    }
  }, [invoicesResponse, currentPage, cursorHistory.length]);

  // Helper function to check if a transaction is stuck (for alert filtering)
  const isTransactionStuck = (invoice: TransformedInvoice): boolean => {
    if (!invoice || !invoice.juniperStatus || !invoice.updatedAt) {
      return false;
    }

    const status = invoice.juniperStatus;
    const updatedAt = new Date(invoice.updatedAt);
    const now = new Date();
    const fourHoursAgo = new Date(now.getTime() - 4 * 60 * 60 * 1000);

    if (isNaN(updatedAt.getTime())) {
      return false;
    }

    return (
      (status?.toLowerCase() === 'processing' && updatedAt < fourHoursAgo) ||
      (status?.toLowerCase() === 'trust request approved' && updatedAt < fourHoursAgo) ||
      (status?.toLowerCase() === 'pdf ready' && updatedAt < fourHoursAgo)
    );
  };

  // Helper function to get bill run period from transaction date
  const getBillRunPeriod = (transactionDate: string) => {
    // Handle invalid date strings
    if (
      !transactionDate ||
      transactionDate === 'Invalid Date' ||
      transactionDate === 'null' ||
      transactionDate === 'undefined' ||
      transactionDate.trim() === ''
    ) {
      return 'No Date';
    }

    const date = new Date(transactionDate);
    if (isNaN(date.getTime())) {
      return 'No Date';
    }

    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.

    // Validate dayOfWeek
    if (isNaN(dayOfWeek) || dayOfWeek < 0 || dayOfWeek > 6) {
      return 'No Date';
    }

    // Calculate Monday of the current week for all transactions
    // This ensures consistent "Week of..." grouping as requested by the user
    const mondayOfCurrentWeek = new Date(date);
    const daysToSubtractForCurrent = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    mondayOfCurrentWeek.setDate(date.getDate() - daysToSubtractForCurrent);

    // Validate the calculated mondayOfCurrentWeek date
    if (isNaN(mondayOfCurrentWeek.getTime())) {
      return 'No Date';
    }

    return `Week of ${mondayOfCurrentWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  // Group transactions by bill run periods
  const groupedInvoices = useMemo(() => {
    if (!invoices || !Array.isArray(invoices)) {
      return [];
    }

    const filtered = [...invoices];

    // Group by bill run periods
    const groups: Array<{ period: string; transactions: any[] }> = [];
    let currentPeriod: string | null = null;
    let currentGroup: any[] = [];

    filtered.forEach(transaction => {
      // Use the transaction's created_at date for grouping
      const billCreatedAt = transaction.createdAt || '';
      const period = getBillRunPeriod(billCreatedAt);

      if (period !== currentPeriod) {
        // Start new group
        if (currentGroup.length > 0) {
          groups.push({ period: currentPeriod || 'No Period', transactions: currentGroup });
        }
        currentPeriod = period;
        currentGroup = [transaction];
      } else {
        currentGroup.push(transaction);
      }
    });

    // Add final group
    if (currentGroup.length > 0) {
      groups.push({ period: currentPeriod || 'No Period', transactions: currentGroup });
    }

    return groups;
  }, [invoices]);

  // --- Grid enhancement state (drawer, active row, hover preview) ---
  const {
    activeRowId,
    activeRow,
    isDrawerOpen,
    activeDrawerTab,
    handleRowClick,
    handleRowDoubleClick,
    handleDrawerTabChange,
    handleDrawerClose,
    openDrawerToTab,
  } = useTransactionDrawerState(groupedInvoices);

  const visibleJuniperIds = useMemo(
    () =>
      activeTab === 'evergreen'
        ? Array.from(
            new Set(
              groupedInvoices
                .flatMap(group => group.transactions)
                .map(transaction => transaction.id)
                .filter(Boolean)
            )
          )
        : [],
    [activeTab, groupedInvoices]
  );

  const {
    data: deliverySummaryResponse,
    isLoading: isLoadingDeliverySummary,
  } = useTransactionDeliverySummary(visibleJuniperIds);

  const deliverySummaryByJuniperId = useMemo(() => {
    const summaryMap = new Map<
      string,
      {
        juniperId: string;
        emailId?: string;
        totalToRecipients: number;
        deliveredToRecipientsCount: number;
        missingToRecipientsCount: number;
        latestSesEventTimestamp?: string;
        statusBucket: string;
      }
    >();
    if (!deliverySummaryResponse?.data) {
      return summaryMap;
    }

    for (const summary of deliverySummaryResponse.data) {
      summaryMap.set(summary.juniperId, summary);
    }
    return summaryMap;
  }, [deliverySummaryResponse]);

  const {
    data: deliveryDetails,
    isLoading: isLoadingDeliveryDetails,
    isFetching: isFetchingDeliveryDetails,
  } = useTransactionDeliveryDetails(
    activeRow?.id || '',
    isDrawerOpen && activeDrawerTab === 'email' && !!activeRow?.id
  );

  const {
    data: documentHistoryResponse,
    isLoading: isLoadingDocumentHistory,
    isFetching: isFetchingDocumentHistory,
  } = useTransactionDocumentHistory(
    activeRow?.id || '',
    isDrawerOpen && activeDrawerTab === 'documents' && !!activeRow?.id
  );

  // Calculate summary statistics for filtered transactions
  const transactionSummary = useMemo(() => {
    const allTransactions = groupedInvoices.flatMap(group => group.transactions);

    // Trust Request calculations
    const trustRequestStats = {
      total: {
        sum: 0,
        count: 0,
      },
      pending: {
        sum: 0,
        count: 0,
      },
      paid: {
        sum: 0,
        count: 0,
      },
      balance: {
        sum: 0,
        count: 0,
      },
    };

    // Operational calculations
    const operationalStats = {
      total: {
        sum: 0,
        count: 0,
      },
      paid: {
        sum: 0,
        count: 0,
      },
      balance: {
        sum: 0,
        count: 0,
      },
    };

    // Status counts for both operational and trust request
    const statusCounts = {
      operational: {} as Record<string, number>,
      trustRequest: {} as Record<string, number>,
    };

    allTransactions.forEach(invoice => {
      // Trust Request calculations and status counts
      if (invoice.trustRequest && invoice.trustRequest.state !== 'not_created') {
        const trustTotal = invoice.trustRequest.total || 0;
        const trustPaid = invoice.trustRequest.paid || 0;
        const trustPending = invoice.trustRequest.pending || 0;
        const trustBalance = invoice.trustRequest.balance;
        const trustState = invoice.trustRequest.state || 'unknown';

        trustRequestStats.total.sum += trustTotal;
        trustRequestStats.total.count += 1;

        if (trustPending > 0) {
          trustRequestStats.pending.sum += trustPending;
          trustRequestStats.pending.count += 1;
        }

        if (trustPaid > 0) {
          trustRequestStats.paid.sum += trustPaid;
          trustRequestStats.paid.count += 1;
        }

        if (trustBalance !== null && trustBalance !== undefined) {
          trustRequestStats.balance.sum += trustBalance;
          trustRequestStats.balance.count += 1;
        }

        // Count by status
        statusCounts.trustRequest[trustState] = (statusCounts.trustRequest[trustState] || 0) + 1;
      }

      // Operational calculations and status counts
      const operationalTotal =
        invoice.operationalBill?.amount || invoice.operationalBill?.total || 0;
      const operationalPaid = invoice.operationalBill?.paid || 0;
      const operationalBalance = invoice.operationalBill?.balance;
      const operationalState = invoice.operationalBill?.state || 'unknown';

      operationalStats.total.sum += operationalTotal;
      operationalStats.total.count += 1;

      if (operationalPaid > 0) {
        operationalStats.paid.sum += operationalPaid;
        operationalStats.paid.count += 1;
      }

      if (operationalBalance !== null && operationalBalance !== undefined) {
        operationalStats.balance.sum += operationalBalance;
        operationalStats.balance.count += 1;
      }

      // Count by status
      statusCounts.operational[operationalState] =
        (statusCounts.operational[operationalState] || 0) + 1;
    });

    return {
      trustRequest: trustRequestStats,
      operational: operationalStats,
      statusCounts,
    };
  }, [groupedInvoices]);

  // Pagination component
  const paginationComponent = useMemo(() => {
    const displayedCount = groupedInvoices.reduce(
      (total, group) => total + group.transactions.length,
      0
    );
    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
    console.log('🔢 Pagination Props:', {
      currentPage,
      hasNextPage,
      hasPreviousPage,
      pageSize,
      displayedCount,
      totalPages,
      totalInvoicesFromAPI: invoices.length,
      isLoading: isLoadingInvoices,
      hasActiveFilter: activeStatusFilters.length > 0,
    });

    return (
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        isLoading={isLoadingInvoices}
        pageSize={pageSize}
        pageSizeOptions={[2, 25, 50, 100, 200]}
        defaultPageSizeOptions={[2, 25, 50, 100, 200]}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onNextPage={handleNextPage}
        onPreviousPage={handlePreviousPage}
        cursorBased={true}
        showRecordCount={true}
        currentItemsCount={invoicesResponse?.pageInfo?.currentCount ?? displayedCount}
      />
    );
  }, [
    currentPage,
    hasNextPage,
    hasPreviousPage,
    pageSize,
    groupedInvoices,
    invoices.length,
    invoicesResponse?.pageInfo?.currentCount,
    isLoadingInvoices,
    activeStatusFilters,
    totalRecords,
    handlePageChange,
    handlePageSizeChange,
    handleNextPage,
    handlePreviousPage,
  ]);

  // Action handlers for the menu actions
  const resendEmail = useResendEmailMutation();
  const releaseEmail = useReleaseEmailMutation();
  const bulkSendEmails = useBulkSendEmailsMutation();
  const regeneratePdf = useRegeneratePdfMutation();
  const bulkRegeneratePdf = useBulkRegeneratePdfMutation();
  const bulkConsolidatePrint = useBulkConsolidatePrintMutation();
  const bulkRefreshFromClio = useBulkRefreshFromClioMutation();
  const bulkSimulate = useBulkSimulateMutation();
  const bulkVoidDelete = useBulkVoidDeleteMutation();
  const markCompleted = useMarkCompletedMutation();
  const bulkMarkCompleted = useBulkMarkCompletedMutation();

  // Active bulk jobs that we're polling for progress. Each mutation handler
  // calls `trackBulkJob(resp)` on accept; the tracker auto-removes itself
  // when the job hits a terminal state (or its ~35-minute budget expires).
  const [activeBulkJobs, setActiveBulkJobs] = useState<string[]>([]);
  const trackBulkJob = useCallback((resp: any) => {
    if (resp?.jobId) {
      setActiveBulkJobs((prev) => (prev.includes(resp.jobId) ? prev : [...prev, resp.jobId]));
    }
  }, []);
  const removeBulkJob = useCallback((jobId: string) => {
    setActiveBulkJobs((prev) => prev.filter((id) => id !== jobId));
  }, []);

  const handleBulkSend = () => {
    if (selection.selectedCount === 0) {
      showToast.warning('No transactions selected');
      return;
    }

    if (selection.mode === 'all') {
      // Sending emails requires per-matter-type template selection and held-count
      // awareness, neither of which we can compute without the full transaction
      // object. Require the user to narrow to a specific selection.
      showToast.warning(
        'Bulk email send requires explicit selection. Use the checkboxes to pick the transactions you want to email (selections persist across pages).'
      );
      return;
    }

    // Filter selected transactions (which include cross-page picks) for resend eligibility
    const eligibleTransactions = selection.getSpecificObjects()
      .filter(inv => isResendEligible(inv) && hasValidRecipients(inv));

    if (eligibleTransactions.length === 0) {
      showToast.warning(
        'None of the selected transactions are eligible for email sending. Only Trust Request bills in awaiting_payment or paid status, Email Held bills, or Completed transactions with valid recipients can be emailed.'
      );
      return;
    }

    const eligibleIds = eligibleTransactions.map(inv => inv.id);

    // Group eligible IDs by matter type for per-type template selection.
    // Also track whether each type group contains only initial sends (email held) or reminders.
    const byType: Record<string, string[]> = {};
    const heldCountByType: Record<string, number> = {};
    for (const inv of eligibleTransactions) {
      const type = inv.matterType === 'Evergreen' ? 'evergreen' : 'operational';
      if (!byType[type]) byType[type] = [];
      byType[type].push(inv.id);
      if (inv.juniperStatus?.toLowerCase() === 'email held') {
        heldCountByType[type] = (heldCountByType[type] || 0) + 1;
      }
    }

    setBulkEligibleIds(eligibleIds);
    setBulkEligibleByType(byType);
    setBulkHeldCountByType(heldCountByType);
    setConfirmTarget({ _bulk: true });
    setConfirmOpen(true);
  };

  // PDF regeneration confirmation helpers
  const openRegenerateConfirm = (type: 'single' | 'bulk', target: any, bulkIds?: string[]) => {
    setRegenerateConfirmType(type);
    setRegenerateConfirmTarget(target);
    setRegenerateConfirmBulkIds(bulkIds || []);
    setRegenerateConfirmOpen(true);
  };

  const handleRegenerateConfirm = () => {
    if (regenerateConfirmType === 'single' && regenerateConfirmTarget) {
      regeneratePdf.mutate(regenerateConfirmTarget.id);
    } else if (regenerateConfirmType === 'bulk' && regenerateConfirmBulkIds.length > 0) {
      bulkRegeneratePdf.mutate(regenerateConfirmBulkIds, { onSuccess: trackBulkJob });
    }
    setRegenerateConfirmOpen(false);
  };

  const handleBulkRegeneratePdf = async () => {
    if (selection.selectedCount === 0) {
      showToast.warning('No transactions selected');
      return;
    }

    let eligibleIds: string[];
    if (selection.mode === 'specific') {
      // We have the full transaction objects for specific picks → filter by eligibility locally.
      eligibleIds = selection.getSpecificObjects()
        .filter(isPdfRegenerationEligible)
        .map(inv => inv.id);
    } else {
      // 'all' mode — backend will skip ineligible items during processing.
      const resolved = await selection.resolveSelectedIds();
      if (resolved.truncated) {
        showToast.warning(
          'Too many matching records for a single bulk action. Narrow the filters and try again.'
        );
        return;
      }
      eligibleIds = resolved.ids;
    }

    if (eligibleIds.length === 0) {
      showToast.warning(
        'None of the selected transactions are eligible for PDF regeneration. Only Trust Request bills in "Awaiting Payment" or "Partial Payment" status, or Operational transactions in "Email Held" or "Completed" status (where the operational bill is not Paid, Voided, or Deleted) can be regenerated.'
      );
      return;
    }

    openRegenerateConfirm('bulk', null, eligibleIds);
  };

  const MAX_CONSOLIDATE_PRINT = 50;

  const handleBulkConsolidatePrint = async () => {
    if (selection.selectedCount === 0) {
      showToast.warning('No transactions selected');
      return;
    }

    if (selection.mode === 'all') {
      // Consolidate-print is capped at MAX_CONSOLIDATE_PRINT. If the matching
      // set exceeds that, the user must narrow filters before proceeding.
      showToast.warning(
        `Consolidate print is limited to ${MAX_CONSOLIDATE_PRINT} transactions. Use the checkboxes to pick specific transactions (max ${MAX_CONSOLIDATE_PRINT}).`
      );
      return;
    }

    const eligibleIds = selection.getSpecificObjects()
      .filter(isConsolidatePrintEligible)
      .map(inv => inv.id);

    if (eligibleIds.length === 0) {
      showToast.warning(
        'None of the selected transactions are eligible for consolidate print. Only Email Held transactions with stored invoice PDFs are eligible.'
      );
      return;
    }

    if (eligibleIds.length > MAX_CONSOLIDATE_PRINT) {
      showToast.warning(
        `You can consolidate a maximum of ${MAX_CONSOLIDATE_PRINT} transactions at a time. Please reduce your selection from ${eligibleIds.length} to ${MAX_CONSOLIDATE_PRINT} or fewer.`
      );
      return;
    }

    bulkConsolidatePrint.mutate(eligibleIds, { onSuccess: trackBulkJob });
  };

  const handleBulkRefreshFromClio = async () => {
    if (selection.selectedCount === 0) {
      showToast.warning('No transactions selected');
      return;
    }

    const resolved = await selection.resolveSelectedIds();
    if (resolved.truncated) {
      showToast.warning(
        'Too many matching records for a single bulk refresh. Narrow the filters and try again.'
      );
      return;
    }

    if (resolved.ids.length === 0) {
      showToast.warning('No transactions selected');
      return;
    }

    bulkRefreshFromClio.mutate(resolved.ids, { onSuccess: trackBulkJob });
  };

  const handleBulkSimulate = async (
    billType: 'operational' | 'trust',
    eventType: 'update' | 'delete'
  ) => {
    if (selection.selectedCount === 0) {
      showToast.warning('No transactions selected');
      return;
    }

    const resolved = await selection.resolveSelectedIds();
    if (resolved.truncated) {
      showToast.warning(
        'Too many matching records for a single bulk simulate. Narrow the filters and try again.'
      );
      return;
    }

    const juniperIds = resolved.ids;
    if (juniperIds.length === 0) {
      showToast.warning('No valid Juniper IDs found in selected transactions');
      return;
    }

    bulkSimulate.mutate(
      {
        juniperIds,
        billType,
        eventType,
        reason: `Bulk simulation from juniper transactions page: ${billType} ${eventType} events for ${juniperIds.length} transaction(s)`,
      },
      { onSuccess: trackBulkJob },
    );
  };

  const handleBulkVoidDelete = () => {
    if (selection.selectedCount === 0) {
      showToast.warning('No transactions selected');
      return;
    }
    if (selection.mode === 'all') {
      // Void/delete eligibility depends on trust request state — not available
      // without full transaction objects. Force specific selection.
      showToast.warning(
        'Bulk void/delete requires explicit selection. Use the checkboxes to pick specific trust requests.'
      );
      return;
    }
    setVoidDeleteTarget(null);
    setVoidDeleteIsBulk(true);
    setVoidDeleteConfirmOpen(true);
  };

  const handleSingleVoidDelete = (invoice: TransformedInvoice) => {
    setVoidDeleteTarget(invoice);
    setVoidDeleteIsBulk(false);
    setVoidDeleteConfirmOpen(true);
  };

  const confirmVoidDelete = () => {
    let juniperIds: string[];

    if (voidDeleteIsBulk) {
      // Void/delete only applies to explicit selection (blocked for 'all' mode above).
      juniperIds = selection.getSpecificObjects()
        .filter(isVoidDeleteEligible)
        .map(inv => inv.id)
        .filter((id): id is string => !!id);
    } else if (voidDeleteTarget) {
      juniperIds = [voidDeleteTarget.id];
    } else {
      return;
    }

    if (juniperIds.length === 0) {
      showToast.warning('No eligible transactions for void/delete');
      return;
    }

    const requestId = `bulk-void-delete-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    bulkVoidDelete.mutate(
      { juniperIds, requestId, reason: 'Void/delete from juniper transactions page' },
      {
        onSuccess: (resp: any) => {
          setVoidDeleteConfirmOpen(false);
          setVoidDeleteTarget(null);
          selection.clear();
          trackBulkJob(resp);
        },
      }
    );
  };

  const handleSelectAll = () => {
    if (!groupedInvoices || groupedInvoices.length === 0) return;
    const visibleTransactions = groupedInvoices.flatMap(group => group.transactions);
    selection.toggleVisible(visibleTransactions);
  };

  const handleToggleSelection = (invoice: TransformedInvoice) => {
    selection.toggle(invoice);
  };

  const handleSelectAllMatching = () => {
    selection.selectAllMatching(queryParams, totalRecords);
  };

  const openConfirmForTransaction = async (transaction: any) => {
    setConfirmTarget(transaction);
    setConfirmOpen(true);
    // Pass matterId to skip DB lookup on the backend
    const matterId = transaction.matterId || transaction.operationalBill?.matter?.id;
    await previewRecipients.loadRecipients(transaction.id, matterId?.toString());
  };

  // PDF viewer handlers
  const handleViewPdf = (type: 'invoice' | 'ledger') => {
    if (!downloadMenuState.transaction) return;

    const juniperId =
      downloadMenuState.transaction.juniperId || downloadMenuState.transaction.xtndTrustId;
    const url = `/juniper/document-viewer/${juniperId}?type=${type}`;

    // Open in new tab
    window.open(url, '_blank');
  };

  const handleViewReceipt = (filename: string) => {
    if (!downloadMenuState.transaction) return;

    const juniperId =
      downloadMenuState.transaction.juniperId || downloadMenuState.transaction.xtndTrustId;
    const url = `/juniper/document-viewer/${juniperId}?type=receipt&filename=${encodeURIComponent(filename)}`;

    // Open in new tab
    window.open(url, '_blank');
  };

  const handleDownloadReceiptsZip = async (transaction: any) => {
    try {
      const juniperId = transaction.juniperId || transaction.xtndTrustId || transaction.id;
      const endpoint = `/juniper-query/transactions/${juniperId}/receipts/zip`;
      const response = await api.post<{ downloadUrl: string }>(endpoint, {});

      if (response.downloadUrl) {
        // Trigger download - backend now includes proper content disposition headers
        const link = document.createElement('a');
        link.href = response.downloadUrl;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        showToast.success('Receipt ZIP download started');
      }
    } catch (error) {
      console.error('Failed to download receipts ZIP:', error);
      showToast.error('Failed to download receipts ZIP');
    }
  };

  const handleQuickViewPdf = (transaction: any) => {
    const hasPdf = transaction.pdfs?.invoice;
    const isValidStatus =
      transaction.juniperStatus?.toLowerCase().includes('email held') ||
      transaction.juniperStatus?.toLowerCase().includes('completed');
    const canView = hasPdf && isValidStatus;

    if (!canView) {
      const reason = !hasPdf
        ? 'PDF not available'
        : `Transaction status must be 'Email Held' or 'Completed' (current: ${transaction.juniperStatus || 'unknown'})`;
      showToast.error(`PDF viewer not available - ${reason}`);
      return;
    }

    const juniperId = transaction.juniperId || transaction.xtndTrustId;
    const url = `/juniper/pdf-viewer/${juniperId}?type=invoice`; // Default to invoice for quick view

    // Open in new tab
    window.open(url, '_blank');
  };

  // Helper function to get trust request number
  const getTrustRequestNumber = (transaction: any) => {
    return transaction.trustRequest?.number || 'N/A';
  };

  // Helper function to format trust request calculation
  const formatTrustRequestCalculation = (invoice: any) => {
    // If trust request hasn't been created, show N/A
    if (invoice.trustRequest?.state === 'not_created') {
      return 'N/A';
    }

    // After transform, calculationData is in camelCase with camelCase properties
    const calculationData = invoice.calculationData;

    if (!calculationData) {
      return 'Calculation data not available';
    }

    // Check if we have stored calculated values - use them directly for consistency
    const { calculatedTrustAmountOpenMatter, calculatedTrustAmountClosedMatter } = calculationData;
    const matterStatus = invoice.matterStatus;

    if (
      calculatedTrustAmountOpenMatter !== undefined &&
      calculatedTrustAmountClosedMatter !== undefined &&
      matterStatus
    ) {
      // Use stored calculated values for exact consistency with creation time
      const relevantAmount =
        matterStatus === 'Closed'
          ? calculatedTrustAmountClosedMatter
          : calculatedTrustAmountOpenMatter;
      const formula =
        matterStatus === 'Closed'
          ? `${numberUtil.formatCurrency(calculationData.outstandingBalances || 0)} - ${numberUtil.formatCurrency(calculationData.trustBalances || 0)}`
          : `${numberUtil.formatCurrency(calculationData.minimumThreshold || 0)} - (${numberUtil.formatCurrency(calculationData.trustBalances || 0)} - ${numberUtil.formatCurrency(calculationData.outstandingBalances || 0)})`;

      return formula;
    }

    // Fallback to recalculation if stored values aren't available (backward compatibility)
    const { minimumThreshold, trustBalances, outstandingBalances } = calculationData;

    if (
      minimumThreshold === undefined ||
      trustBalances === undefined ||
      outstandingBalances === undefined
    ) {
      return 'Calculation data not available';
    }

    // Format the calculation with actual values from calculation data
    const calculation = `${numberUtil.formatCurrency(minimumThreshold)} - (${numberUtil.formatCurrency(trustBalances)} - ${numberUtil.formatCurrency(outstandingBalances)})`;
    return calculation;
  };

  // Helper function to copy text to clipboard
  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast.success(`${label} copied to clipboard!`);
    } catch (error) {
      showToast.error('Failed to copy to clipboard');
    }
  };

  const getVisibleColumnCount = () => {
    return Object.values(visibleColumns).filter(Boolean).length;
  };

  const getTotalColumnCount = () => {
    return Object.keys(visibleColumns).length;
  };

  // Calculate dynamic colSpan values based on visible columns
  const getHeaderSpans = () => {
    // Matter Information section (always includes: warning, matterNumber, juniperStatus, lastSent)
    let matterInfoCols = 4; // Base columns always visible (warning + 3 others)
    if (visibleColumns.matterType) matterInfoCols++;
    if (visibleColumns.clientName) matterInfoCols++;
    if (visibleColumns.createdAt) matterInfoCols++;
    if (visibleColumns.updatedAt) matterInfoCols++;
    if (visibleColumns.delivery) matterInfoCols++;
    if (visibleColumns.paymentLink) matterInfoCols++;

    // Operational Bill section (always includes: number, status)
    let operationalBillCols = 2; // Base columns always visible
    if (visibleColumns.operationalBillTotal) operationalBillCols++;
    if (visibleColumns.operationalBillPaid) operationalBillCols++;
    if (visibleColumns.operationalBillBalance) operationalBillCols++;
    if (visibleColumns.operationalBillDue) operationalBillCols++;
    if (visibleColumns.operationalBillIssuedAt) operationalBillCols++;
    if (visibleColumns.operationalBillDueAt) operationalBillCols++;
    if (visibleColumns.lastOperationalPaymentAt) operationalBillCols++;

    // Trust Request Bill section (always includes: number, status)
    let trustRequestBillCols = 2; // Base columns always visible
    if (visibleColumns.trustRequestBillTotal) trustRequestBillCols++;
    if (visibleColumns.trustRequestBillPaid) trustRequestBillCols++;
    if (visibleColumns.trustRequestBillBalance) trustRequestBillCols++;
    if (visibleColumns.trustRequestBillDue) trustRequestBillCols++;
    if (visibleColumns.trustRequestBillPending) trustRequestBillCols++;
    if (visibleColumns.trustRequestBillIssuedAt) trustRequestBillCols++;
    if (visibleColumns.trustRequestBillDueAt) trustRequestBillCols++;
    if (visibleColumns.lastTrustRequestPaymentAt) trustRequestBillCols++;
    if (visibleColumns.trustRequestCalculation) trustRequestBillCols++;
    if (visibleColumns.trustRequestEmailType) trustRequestBillCols++;

    // Actions section (always 1)
    const actionsCols = 1;

    return {
      matterInfo: matterInfoCols,
      operationalBill: operationalBillCols,
      trustRequestBill: trustRequestBillCols,
      actions: actionsCols,
      total: matterInfoCols + operationalBillCols + trustRequestBillCols + actionsCols,
    };
  };

  // Combined loading state
  const isLoading = isLoadingInvoices || isLoadingCounts || isLoadingUsers;

  return (
    <PageLayout
      title="Juniper Transactions"
      description="View and manage invoice transactions from Clio Manage"
      icon="HiTable"
      isLoading={isLoading}
      headerContent={
        <Button
          variant="primary"
          size="md"
          onClick={() => setIsWebhookModalOpen(true)}
          className="flex items-center justify-center w-10 h-10 relative z-40 pointer-events-auto"
          title="Submit Request"
        >
          <Icon name="HiPlus" size="lg" />
        </Button>
      }
    >

      {/* Tab Navigation */}
      <div
        role="tablist"
        className="
          mx-auto self-center
          max-w-full w-fit
          flex flex-wrap justify-center gap-1
          bg-xtnd-dark-50 dark:bg-xtnd-dark-800 border border-xtnd-dark-100 dark:border-xtnd-dark-600
          p-1 rounded-lg mb-6
        "
      >
        <Button
          type="button"
          role="tab"
          aria-selected={activeTab === 'evergreen'}
          onClick={() => setActiveTab('evergreen')}
          size="sm"
          variant="ghost"
          className={`${
            activeTab === 'evergreen'
              ? 'bg-xtnd-blue text-xtnd-white shadow-sm'
              : 'text-xtnd-dark-600 dark:text-xtnd-light-400 hover:text-xtnd-dark dark:hover:text-xtnd-white hover:bg-xtnd-dark-100 dark:hover:bg-xtnd-dark-700'
          }`}
          leftIcon={<Icon name="HiCollection" className="h-4 w-4" />}
        >
          Evergreen
        </Button>
        <Button
          type="button"
          role="tab"
          aria-selected={activeTab === 'trust-requests'}
          onClick={() => setActiveTab('trust-requests')}
          size="sm"
          variant="ghost"
          className={`${
            activeTab === 'trust-requests'
              ? 'bg-xtnd-blue text-xtnd-white shadow-sm'
              : 'text-xtnd-dark-600 dark:text-xtnd-light-400 hover:text-xtnd-dark dark:hover:text-xtnd-white hover:bg-xtnd-dark-100 dark:hover:bg-xtnd-dark-700'
          }`}
          leftIcon={<Icon name="HiDocumentText" className="h-4 w-4" />}
        >
          Trust Requests
        </Button>
      </div>

      {/* Column Visibility Modal */}
      <ColumnVisibilityModal
        isOpen={isColumnModalOpen}
        onClose={() => setIsColumnModalOpen(false)}
        visibleColumns={visibleColumns}
        onToggleColumn={(key) => {
          setVisibleColumns((prev: Record<string, boolean>) => ({
            ...prev,
            [key]: !prev[key],
          }));
        }}
        onToggleAllColumns={(checked) => {
          setVisibleColumns((prev: Record<string, boolean>) => {
            const newState = { ...prev };
            Object.keys(newState).forEach(key => {
              newState[key] = checked;
            });
            return newState;
          });
        }}
      />

      {/* Active bulk-job progress trackers — one pill per in-flight job. */}
      {activeBulkJobs.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-2">
          {activeBulkJobs.map((jobId) => (
            <BulkJobProgressTracker
              key={jobId}
              jobId={jobId}
              endpoint="juniper"
              onDone={removeBulkJob}
            />
          ))}
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'evergreen' ? (
        <>
          {/* Collapsible Status Cards Section */}
      <Card variant="default" className="w-full">
        <CardBody>
          <button
            className="flex items-center justify-between w-full text-left focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2"
            onClick={() => setIsCardsCollapsed(!isCardsCollapsed)}
            aria-expanded={!isCardsCollapsed}
            aria-controls="cards-content"
          >
            <div className="flex items-center space-x-3">
              <Icon name="HiViewGrid" className="h-5 w-5 text-xtnd-dark dark:text-xtnd-light" />
              <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                Status Filter
              </h3>
              <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400">
                • {getSelectedCardsSummary()}
              </span>
            </div>
            <Icon
              name={isCardsCollapsed ? "HiChevronDown" : "HiChevronUp"}
              className="h-5 w-5 text-xtnd-dark-400 dark:text-xtnd-dark-500 transition-transform duration-200"
            />
          </button>

            {!isCardsCollapsed && (
              <div id="cards-content" className="mt-4">
                {/* Status Overview Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
                  <Card
                    className={`
            bg-white dark:bg-xtnd-dark-800 border border-xtnd-light dark:border-xtnd-dark-700
            hover:shadow-2xl hover:shadow-red-500/20 hover:-translate-y-1
            transition-all duration-300 rounded-xtnd cursor-pointer
            ${activeStatusFilters.includes('alert') ? 'ring-2 ring-red-500 shadow-lg shadow-red-500/30 -translate-y-0.5' : ''}
          `}
                    onClick={() => handleStatusCardClick('alert')}
                  >
                    <CardBody className="p-4">
                      <div className="grid grid-cols-2 grid-rows-2 gap-2 h-full min-h-[80px]">
                        <div className="col-span-2">
                          <p className="text-base font-medium text-xtnd-dark dark:text-xtnd-light">
                            Alert
                          </p>
                        </div>
                        <div className="flex items-center justify-start">
                          <Icon name="HiExclamationCircle" className="h-8 w-8 text-red-500" />
                        </div>
                        <div className="flex items-center justify-end">
                          <p className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-light">
                            {isLoadingCounts ? '...' : counts.alert}
                          </p>
                        </div>
                      </div>
                    </CardBody>
                  </Card>

                  <Card
                    className={`
            bg-white dark:bg-xtnd-dark-800 border border-xtnd-light dark:border-xtnd-dark-700
            hover:shadow-2xl hover:shadow-xtnd-dark-400/20 hover:-translate-y-1
            transition-all duration-300 rounded-xtnd cursor-pointer
            ${activeStatusFilters.includes('deletedBills') ? 'ring-2 ring-xtnd-dark-400 shadow-lg shadow-xtnd-dark-400/30 -translate-y-0.5' : ''}
          `}
                    onClick={() => handleStatusCardClick('deletedBills')}
                  >
                    <CardBody className="p-4">
                      <div className="grid grid-cols-2 grid-rows-2 gap-2 h-full min-h-[80px]">
                        <div className="col-span-2">
                          <p className="text-base font-medium text-xtnd-dark dark:text-xtnd-light">
                            Deleted & Voided Bills
                          </p>
                        </div>
                        <div className="flex items-center justify-start">
                          <Icon name="HiTrash" className="h-8 w-8 text-xtnd-dark-400" />
                        </div>
                        <div className="flex items-center justify-end">
                          <p className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-light">
                            {isLoadingCounts ? '...' : counts.deletedBills || 0}
                          </p>
                        </div>
                      </div>
                    </CardBody>
                  </Card>

                  <Card
                    className={`
            bg-white dark:bg-xtnd-dark-800 border border-xtnd-light dark:border-xtnd-dark-700
            hover:shadow-2xl hover:shadow-cyan-500/20 hover:-translate-y-1
            transition-all duration-300 rounded-xtnd cursor-pointer
            ${activeStatusFilters.includes('inProcess') ? 'ring-2 ring-cyan-500 shadow-lg shadow-cyan-500/30 -translate-y-0.5' : ''}
          `}
                    onClick={() => handleStatusCardClick('inProcess')}
                  >
                    <CardBody className="p-4">
                      <div className="grid grid-cols-2 grid-rows-2 gap-2 h-full min-h-[80px]">
                        <div className="col-span-2">
                          <p className="text-base font-medium text-xtnd-dark dark:text-xtnd-light">
                            In Process
                          </p>
                        </div>
                        <div className="flex items-center justify-start">
                          <Icon name="HiRefresh" className="h-8 w-8 text-cyan-500" />
                        </div>
                        <div className="flex items-center justify-end">
                          <p className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-light">
                            {isLoadingCounts ? '...' : counts.inProcess || 0}
                          </p>
                        </div>
                      </div>
                    </CardBody>
                  </Card>

                  <Card
                    className={`
            bg-white dark:bg-xtnd-dark-800 border border-xtnd-light dark:border-xtnd-dark-700
            hover:shadow-2xl hover:shadow-orange-500/20 hover:-translate-y-1
            transition-all duration-300 rounded-xtnd cursor-pointer
            ${activeStatusFilters.includes('operationalPreApproval') ? 'ring-2 ring-orange-500 shadow-lg shadow-orange-500/30 -translate-y-0.5' : ''}
          `}
                    onClick={() => handleStatusCardClick('operationalPreApproval')}
                  >
                    <CardBody className="p-4">
                      <div className="grid grid-cols-2 grid-rows-2 gap-2 h-full min-h-[80px]">
                        <div className="col-span-2">
                          <p className="text-base font-medium text-xtnd-dark dark:text-xtnd-light">
                            Operational Pre Approval
                          </p>
                        </div>
                        <div className="flex items-center justify-start">
                          <Icon name="HiClipboard" className="h-8 w-8 text-orange-500" />
                        </div>
                        <div className="flex items-center justify-end">
                          <p className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-light">
                            {isLoadingCounts ? '...' : counts.operationalPreApproval || 0}
                          </p>
                        </div>
                      </div>
                    </CardBody>
                  </Card>

                  <Card
                    className={`
            bg-white dark:bg-xtnd-dark-800 border border-xtnd-light dark:border-xtnd-dark-700
            hover:shadow-2xl hover:shadow-green-500/20 hover:-translate-y-1
            transition-all duration-300 rounded-xtnd cursor-pointer
            ${activeStatusFilters.includes('trrCreated') ? 'ring-2 ring-green-500 shadow-lg shadow-green-500/30 -translate-y-0.5' : ''}
          `}
                    onClick={() => handleStatusCardClick('trrCreated')}
                  >
                    <CardBody className="p-4">
                      <div className="grid grid-cols-2 grid-rows-2 gap-2 h-full min-h-[80px]">
                        <div className="col-span-2">
                          <p className="text-base font-medium text-xtnd-dark dark:text-xtnd-light">
                            Pending TRR Approval
                          </p>
                        </div>
                        <div className="flex items-center justify-start">
                          <Icon name="HiDocumentAdd" className="h-8 w-8 text-green-500" />
                        </div>
                        <div className="flex items-center justify-end">
                          <p className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-light">
                            {isLoadingCounts ? '...' : counts.trrCreated}
                          </p>
                        </div>
                      </div>
                    </CardBody>
                  </Card>

                  <Card
                    className={`
            bg-white dark:bg-xtnd-dark-800 border border-xtnd-light dark:border-xtnd-dark-700
            hover:shadow-2xl hover:shadow-yellow-500/20 hover:-translate-y-1
            transition-all duration-300 rounded-xtnd cursor-pointer
            ${activeStatusFilters.includes('pending') ? 'ring-2 ring-yellow-500 shadow-lg shadow-yellow-500/30 -translate-y-0.5' : ''}
          `}
                    onClick={() => handleStatusCardClick('pending')}
                  >
                    <CardBody className="p-4">
                      <div className="grid grid-cols-2 grid-rows-2 gap-2 h-full min-h-[80px]">
                        <div className="col-span-2">
                          <p className="text-base font-medium text-xtnd-dark dark:text-xtnd-light">
                            Juniper Email on Hold
                          </p>
                        </div>
                        <div className="flex items-center justify-start">
                          <Icon name="HiClock" className="h-8 w-8 text-yellow-500" />
                        </div>
                        <div className="flex items-center justify-end">
                          <p className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-light">
                            {isLoadingCounts ? '...' : counts.pending}
                          </p>
                        </div>
                      </div>
                    </CardBody>
                  </Card>

                  <Card
                    className={`
            bg-white dark:bg-xtnd-dark-800 border border-xtnd-light dark:border-xtnd-dark-700
            hover:shadow-2xl hover:shadow-blue-500/20 hover:-translate-y-1
            transition-all duration-300 rounded-xtnd cursor-pointer
            ${activeStatusFilters.includes('sent') ? 'ring-2 ring-blue-500 shadow-lg shadow-blue-500/30 -translate-y-0.5' : ''}
          `}
                    onClick={() => handleStatusCardClick('sent')}
                  >
                    <CardBody className="p-4">
                      <div className="grid grid-cols-2 grid-rows-2 gap-2 h-full min-h-[80px]">
                        <div className="col-span-2">
                          <p className="text-base font-medium text-xtnd-dark dark:text-xtnd-light">
                            Juniper Completed
                          </p>
                        </div>
                        <div className="flex items-center justify-start">
                          <Icon name="HiDocumentText" className="h-8 w-8 text-blue-500" />
                        </div>
                        <div className="flex items-center justify-end">
                          <p className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-light">
                            {isLoadingCounts ? '...' : counts.sent}
                          </p>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </div>

                {/* Active Filter Indicator with Clear/Reset Controls */}
                <div className="flex items-center justify-center gap-3">
                  <div className="bg-xtnd-light dark:bg-xtnd-dark-700 px-4 py-2 rounded-full">
                    <span className="text-sm text-xtnd-dark dark:text-xtnd-light font-medium">
                      {getSelectedCardsSummary()}
                    </span>
                  </div>
                  
                  {/* Clear All Button - shows when any filters are active */}
                  {activeStatusFilters.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setActiveStatusFilters([]);
                        setSelectedOperationalStatuses([]);
                        setSelectedTrustRequestStatuses([]);
                        setSelectedUserIds([]);
                        setSelectedMatterType('all');
                        setSelectedMatterId('');
                        setMatterOptions([]);
                        setBillNumberFilter('');
                        setPastDueOnly(false);
                        setNoPaymentInDays(undefined);
                        setSelectedJuniperStatuses([]);
                        setSelectedSortField('createdAt');
                        setSelectedSortDirection('desc');
                        // Reset pending Matter/Sort state too
                        setPendingMatterId('');

                        setPendingBillNumber('');
                        setPendingSortField('createdAt');
                        setPendingSortDirection('desc');
                        setPendingPastDue(false);
                        setPendingNoPaymentInDays(undefined);
                        const newSearchParams = new URLSearchParams(searchParams);
                        newSearchParams.delete('statusFilters');
                        newSearchParams.delete('matterType');
                        newSearchParams.delete('matterId');
                        newSearchParams.delete('billNumber');
                        newSearchParams.delete('pastDue');
                        newSearchParams.delete('noPaymentInDays');
                        newSearchParams.delete('juniperStatuses');
                        newSearchParams.delete('sortField');
                        newSearchParams.delete('sortDirection');
                        setSearchParams(newSearchParams);
                        updateSubFilterUrlParams([], [], []);
                        resetPagination();
                      }}
                      leftIcon={<Icon name="HiX" className="h-4 w-4" />}
                    >
                      Clear All
                    </Button>
                  )}
                  
                  {/* Reset to Defaults Button - shows when filters differ from defaults */}
                  {activeStatusFilters.length === 0 && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        setActiveStatusFilters(DEFAULT_ACTIVE_FILTERS);
                        setSelectedSortField('createdAt');
                        setSelectedSortDirection('desc');
                        setPendingSortField('createdAt');
                        setPendingSortDirection('desc');
                        const newSearchParams = new URLSearchParams(searchParams);
                        newSearchParams.set('statusFilters', DEFAULT_ACTIVE_FILTERS.join(','));
                        newSearchParams.set('sortField', 'createdAt');
                        newSearchParams.set('sortDirection', 'desc');
                        setSearchParams(newSearchParams);
                        resetPagination();
                      }}
                      leftIcon={<Icon name="HiRefresh" className="h-4 w-4" />}
                    >
                      Reset to Defaults
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Collapsible Filter Section */}
        <Card variant="default" className="w-full">
          <CardBody>
            <button
              className="flex items-center justify-between w-full text-left focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2"
              onClick={() => setIsFiltersCollapsed(!isFiltersCollapsed)}
              aria-expanded={!isFiltersCollapsed}
              aria-controls="filters-content"
            >
              <div className="flex items-center space-x-3">
                <Icon name="HiFilter" className="h-5 w-5 text-xtnd-dark dark:text-xtnd-light" />
                <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                  Filters
                </h3>
                <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400">
                  • {getDateRangeSummary()} • {getSelectedFiltersSummary()}
                </span>
              </div>
              <Icon
                name={isFiltersCollapsed ? 'HiChevronDown' : 'HiChevronUp'}
                className="h-5 w-5 text-xtnd-dark-400 dark:text-xtnd-dark-500 transition-transform duration-200"
              />
            </button>

            {!isFiltersCollapsed && (
              <div id="filters-content" className="mt-4">
                {/* Responsive Filter Cards Container */}
                <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-4">
                  {/* Date Range Selector */}
                  <Card variant="default" className="w-full">
                    <CardBody>
                      <div className="flex flex-col gap-4">
                        <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                          Date Range
                        </h3>
                        <div className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-xtnd-dark-600 dark:text-xtnd-dark-400">
                            Filter by
                          </span>
                          <div className="flex flex-wrap gap-2">
                            {([
                              { value: 'createdAt', label: 'Created Date' },
                              { value: 'issuedAt', label: 'Issued Date' },
                            ] as const).map(option => (
                              <button
                                key={option.value}
                                onClick={() => {
                                  updateDateField(option.value);
                                  resetPagination();
                                }}
                                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${
                                  dateField === option.value
                                    ? 'bg-xtnd-blue text-white ring-2 ring-offset-1 ring-xtnd-blue'
                                    : 'bg-xtnd-light dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-light hover:bg-xtnd-blue hover:text-white'
                                }`}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <DatePicker
                          mode="range"
                          label=""
                          value={{
                            startDate: startDate ? new Date(startDate) : null,
                            endDate: endDate ? new Date(endDate) : null,
                          }}
                          onChange={range => {
                            // Only update when both dates are selected
                            if (range.startDate && range.endDate) {
                              updateDateRange(
                                dateUtil.formatDate(range.startDate, dateUtil.DateFormat.ISO),
                                dateUtil.formatDate(range.endDate, dateUtil.DateFormat.ISO)
                              );
                              resetPagination();
                            }
                          }}
                          format={dateUtil.DateFormat.US}
                        />

                        {/* Data Summary */}
                        <div className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400">
                          {activeStatusFilters.length === 0 ? (
                            <span className="text-yellow-600 dark:text-yellow-400 font-medium">
                              No filters selected - no transactions shown
                            </span>
                          ) : (
                            <>
                              Showing {invoicesResponse?.pageInfo?.totalMatching ?? invoices.length} matching transaction{(invoicesResponse?.pageInfo?.totalMatching ?? invoices.length) !== 1 ? 's' : ''}
                              {activeStatusFilters.length > 0 && 
                                ` (${activeStatusFilters.length} filter${activeStatusFilters.length !== 1 ? 's' : ''} active)`}
                            </>
                          )}
                        </div>
                      </div>
                    </CardBody>
                  </Card>

                  {/* Matter Type Filter - Evergreen tab only */}
                  {activeTab === 'evergreen' && (
                    <Card variant="default" className="w-full">
                      <CardBody>
                        <div className="flex flex-col gap-4">
                          <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                            Matter Type
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {(['all', 'Evergreen', 'Operational'] as const).map(type => (
                              <button
                                key={type}
                                onClick={() => {
                                  setSelectedMatterType(type);
                                  updateMatterTypeParam(type);
                                  resetPagination();
                                }}
                                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${
                                  selectedMatterType === type
                                    ? 'bg-xtnd-blue text-white ring-2 ring-offset-1 ring-xtnd-blue'
                                    : 'bg-xtnd-light dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-light hover:bg-xtnd-blue hover:text-white'
                                }`}
                              >
                                {type === 'all' ? 'All' : type}
                              </button>
                            ))}
                          </div>
                          {selectedMatterType !== 'all' && (
                            <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-400">
                              Showing {selectedMatterType} matters only
                            </p>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  )}

                  <Card variant="default" className="w-full">
                    <CardBody>
                      <div className="flex flex-col gap-4">
                        <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                          Matter / Sort
                        </h3>
                        <Select
                          label="Matter"
                          options={matterOptions}
                          value={
                            pendingMatterId
                              ? matterOptions.find(o => o.value === pendingMatterId) || { value: pendingMatterId, label: pendingMatterId }
                              : null
                          }
                          onChange={(option) => {
                            const id = (option as { value: string } | null)?.value || '';
                            setPendingMatterId(id);
                          }}
                          onInputChange={(val, meta) => {
                            if (meta.action === 'input-change') handleMatterSearch(val);
                          }}
                          placeholder="Search by display number..."
                          searchable
                          clearable
                          isLoading={matterSearchLoading}
                          noOptionsMessage={() => 'Type to search matters'}
                          fullWidth
                        />
                        <label className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-300">
                          Bill Number
                          <input
                            type="text"
                            value={pendingBillNumber}
                            onChange={event => setPendingBillNumber(event.target.value)}
                            onKeyDown={event => {
                              if (event.key === 'Enter') applyMatterSortFilters();
                            }}
                            placeholder="Exact bill number"
                            className="mt-1 w-full rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-600 bg-white dark:bg-xtnd-dark-700 px-3 py-2 text-sm"
                          />
                        </label>
                        <label className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-300">
                          Sort Field
                          <select
                            value={pendingSortField}
                            onChange={event => {
                              setPendingSortField(event.target.value as typeof selectedSortField);
                            }}
                            className="mt-1 w-full rounded-md border border-xtnd-dark-200 dark:border-xtnd-dark-600 bg-white dark:bg-xtnd-dark-700 px-3 py-2 text-sm"
                          >
                            <option value="createdAt">Created</option>
                            <option value="lastSentAt">Last Sent</option>
                            <option value="operationalBillBalance">Operational Balance</option>
                            <option value="trustRequestBalance">Trust Balance</option>
                            <option value="lastOperationalPaymentAt">Last Op. Payment</option>
                            <option value="lastTrustRequestPaymentAt">Last TRR Payment</option>
                          </select>
                        </label>
                        <div className="flex gap-2">
                          {(['desc', 'asc'] as const).map(direction => (
                            <button
                              key={direction}
                              onClick={() => setPendingSortDirection(direction)}
                              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${
                                pendingSortDirection === direction
                                  ? 'bg-xtnd-blue text-white ring-2 ring-offset-1 ring-xtnd-blue'
                                  : 'bg-xtnd-light dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-light hover:bg-xtnd-blue hover:text-white'
                              }`}
                            >
                              {direction === 'desc' ? 'Descending' : 'Ascending'}
                            </button>
                          ))}
                        </div>
                        <label className="inline-flex items-center gap-2 text-sm text-xtnd-dark-600 dark:text-xtnd-dark-300">
                          <input
                            type="checkbox"
                            checked={pendingPastDue}
                            onChange={event => setPendingPastDue(event.target.checked)}
                            className="h-4 w-4 rounded border-xtnd-dark-300"
                          />
                          Past due only
                        </label>
                        <label className="inline-flex items-center gap-2 text-sm text-xtnd-dark-600 dark:text-xtnd-dark-300">
                          Last payment
                          <input
                            type="number"
                            min="1"
                            placeholder="days"
                            value={pendingNoPaymentInDays ?? ''}
                            onChange={event => {
                              const val = event.target.value ? parseInt(event.target.value, 10) : undefined;
                              setPendingNoPaymentInDays(val && val > 0 ? val : undefined);
                            }}
                            onKeyDown={event => {
                              if (event.key === 'Enter') applyMatterSortFilters();
                            }}
                            className="w-16 h-8 rounded border border-xtnd-dark-300 dark:border-xtnd-dark-600 bg-xtnd-light dark:bg-xtnd-dark-800 text-center text-sm"
                          />
                          or more days ago
                        </label>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={applyMatterSortFilters}
                          disabled={!hasPendingMatterSortChanges}
                          className="w-full"
                        >
                          {hasPendingMatterSortChanges ? 'Apply' : 'Applied'}
                        </Button>
                      </div>
                    </CardBody>
                  </Card>

                  <Card variant="default" className="w-full">
                    <CardBody>
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                          Juniper Status
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {[
                            'processing',
                            'email held',
                            'completed',
                            'failed',
                            'trust pending approval',
                            'trust request approved',
                            'pdf ready',
                            'trust request deleted',
                            'trust request voided',
                          ].map(status => (
                            <button
                              key={status}
                              onClick={() => {
                                const nextStatuses = selectedJuniperStatuses.includes(status)
                                  ? selectedJuniperStatuses.filter(item => item !== status)
                                  : [...selectedJuniperStatuses, status];
                                setSelectedJuniperStatuses(nextStatuses);
                                const next = new URLSearchParams(searchParams);
                                if (nextStatuses.length > 0) {
                                  next.set('juniperStatuses', nextStatuses.join(','));
                                } else {
                                  next.delete('juniperStatuses');
                                }
                                setSearchParams(next);
                                resetPagination();
                              }}
                              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${
                                selectedJuniperStatuses.includes(status)
                                  ? 'bg-indigo-100 text-indigo-900 dark:bg-indigo-900 dark:text-indigo-200 ring-2 ring-offset-1 ring-indigo-300'
                                  : 'bg-xtnd-light dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-light hover:bg-xtnd-blue hover:text-white'
                              }`}
                            >
                              {formatRawStatus(status)}
                            </button>
                          ))}
                        </div>
                      </div>
                    </CardBody>
                  </Card>

                  {/* Bill Status Filters - Hidden */}
                  {showHiddenCards && <Card variant="default" className="w-full">
                    <CardBody>
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                          Bill Status
                        </h3>

                        {/* Operational Status Filters */}
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 flex items-center gap-2">
                            <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                            Operational Status
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {[
                              { value: 'draft', label: 'Draft', color: 'bg-yellow-100' },
                              {
                                value: 'awaiting_approval',
                                label: 'Awaiting Approval',
                                color: 'bg-yellow-100',
                              },
                              {
                                value: 'awaiting_payment',
                                label: 'Awaiting Payment',
                                color: 'bg-yellow-100',
                              },
                              {
                                value: 'partial_payment',
                                label: 'Partial Payment',
                                color: 'bg-blue-100',
                              },
                              {
                                value: 'pending_payment',
                                label: 'Pending Payment',
                                color: 'bg-lime-100',
                              },
                              { value: 'paid', label: 'Paid', color: 'bg-green-100' },
                              { value: 'not_created', label: 'Not Created', color: 'bg-xtnd-dark-50' },
                              { value: 'void', label: 'Void', color: 'bg-red-100' },
                              { value: 'deleted', label: 'Deleted', color: 'bg-red-100' },
                            ].map(status => (
                              <button
                                key={status.value}
                                onClick={() => handleOperationalStatusToggle(status.value)}
                                className={`
                                px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200
                                ${
                                  selectedOperationalStatuses.includes(status.value)
                                    ? `${status.color} ${getFilterButtonTextColor(status.value)} ring-2 ring-offset-1 ring-${status.color.split('-')[1]}-300`
                                    : `bg-xtnd-light dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-light hover:bg-xtnd-blue hover:text-white`
                                }
                              `}
                              >
                                {status.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Trust Request Status Filters */}
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-green-700 dark:text-green-300 flex items-center gap-2">
                            <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                            Trust Request Status
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {[
                              { value: 'not_created', label: 'Not Created', color: 'bg-xtnd-dark-50' },
                              { value: 'draft', label: 'Draft', color: 'bg-yellow-100' },
                              {
                                value: 'awaiting_approval',
                                label: 'Awaiting Approval',
                                color: 'bg-yellow-100',
                              },
                              {
                                value: 'awaiting_payment',
                                label: 'Awaiting Payment',
                                color: 'bg-yellow-100',
                              },
                              {
                                value: 'partial_payment',
                                label: 'Partial Payment',
                                color: 'bg-blue-100',
                              },
                              {
                                value: 'pending_payment',
                                label: 'Pending Payment',
                                color: 'bg-lime-100',
                              },
                              { value: 'paid', label: 'Paid', color: 'bg-green-100' },
                              { value: 'void', label: 'Void', color: 'bg-red-100' },
                              { value: 'deleted', label: 'Deleted', color: 'bg-red-100' },
                            ].map(status => (
                              <button
                                key={status.value}
                                onClick={() => handleTrustRequestStatusToggle(status.value)}
                                className={`
                                px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200
                                ${
                                  selectedTrustRequestStatuses.includes(status.value)
                                    ? `${status.color} ${getFilterButtonTextColor(status.value)} ring-2 ring-offset-1 ring-${status.color.split('-')[1]}-300`
                                    : `bg-xtnd-light dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-light hover:bg-xtnd-blue hover:text-white`
                                }
                              `}
                              >
                                {status.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Clear Sub-Filters */}
                        {(selectedOperationalStatuses.length > 0 ||
                          selectedTrustRequestStatuses.length > 0 ||
                          selectedUserIds.length > 0) && (
                          <div className="flex justify-end">
                            <button
                              onClick={() => {
                                setSelectedOperationalStatuses([]);
                                setSelectedTrustRequestStatuses([]);
                                setSelectedUserIds([]);
                                updateSubFilterUrlParams([], [], []);
                                resetPagination();
                              }}
                              className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400 hover:text-red-500 transition-colors duration-200"
                            >
                              Clear all sub-filters
                            </button>
                          </div>
                        )}
                      </div>
                    </CardBody>
                  </Card>}

                  {/* Custom Field Filters */}
                  {customFieldFilterOptions.length > 0 && (
                    <Card variant="default" className="w-full">
                      <CardBody>
                        <div className="space-y-4">
                          <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                            Custom Field Filters
                          </h3>

                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                            {customFieldFilterOptions.map((filter) => (
                              <div key={filter.fieldId} className="flex flex-col gap-1.5 min-w-0">
                                <label className="text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-300 truncate" title={filter.fieldName}>
                                  {filter.fieldName}
                                </label>
                                {filter.fieldType === 'checkbox' ? (
                                  (() => {
                                    const currentValues = customFieldSelections[filter.fieldId] || [];
                                    const state = currentValues.includes('true') ? 'yes' : currentValues.includes('false') ? 'no' : 'off';
                                    return (
                                      <button
                                        onClick={() => {
                                          setCustomFieldSelections((prev) => {
                                            let next: string[];
                                            if (state === 'off') next = ['true'];
                                            else if (state === 'yes') next = ['false'];
                                            else next = [];
                                            const updated = { ...prev, [filter.fieldId]: next };
                                            const newSearchParams = new URLSearchParams(searchParams);
                                            if (next.length > 0) {
                                              newSearchParams.set(`cf_${filter.fieldId}`, next.join(','));
                                            } else {
                                              newSearchParams.delete(`cf_${filter.fieldId}`);
                                            }
                                            setSearchParams(newSearchParams);
                                            resetPagination();
                                            return updated;
                                          });
                                        }}
                                        className={`
                                          w-full px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200
                                          ${state === 'yes'
                                            ? 'bg-green-600 text-white ring-2 ring-offset-1 ring-green-600/50'
                                            : state === 'no'
                                              ? 'bg-red-500 text-white ring-2 ring-offset-1 ring-red-500/50'
                                              : 'bg-xtnd-light dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-light hover:bg-xtnd-blue hover:text-white'
                                          }
                                        `}
                                      >
                                        {state === 'yes' ? 'Yes' : state === 'no' ? 'No' : 'All'}
                                      </button>
                                    );
                                  })()
                                ) : (
                                  <div className="max-h-28 overflow-y-auto space-y-1">
                                    {filter.options.map((option) => {
                                      const isSelected = (customFieldSelections[filter.fieldId] || []).includes(option);
                                      return (
                                        <button
                                          key={option}
                                          onClick={() => {
                                            setCustomFieldSelections((prev) => {
                                              const current = prev[filter.fieldId] || [];
                                              const next = isSelected
                                                ? current.filter((v) => v !== option)
                                                : [...current, option];
                                              const updated = { ...prev, [filter.fieldId]: next };
                                              const newSearchParams = new URLSearchParams(searchParams);
                                              if (next.length > 0) {
                                                newSearchParams.set(`cf_${filter.fieldId}`, next.join(','));
                                              } else {
                                                newSearchParams.delete(`cf_${filter.fieldId}`);
                                              }
                                              setSearchParams(newSearchParams);
                                              resetPagination();
                                              return updated;
                                            });
                                          }}
                                          className={`
                                            w-full text-left px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 truncate
                                            ${isSelected
                                              ? 'bg-xtnd-blue text-white ring-2 ring-offset-1 ring-xtnd-blue/50'
                                              : 'bg-xtnd-light dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-light hover:bg-xtnd-blue hover:text-white'
                                            }
                                          `}
                                          title={option}
                                        >
                                          {option}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Clear Custom Field Filters */}
                          {Object.values(customFieldSelections).some((v) => v.length > 0) && (
                            <div className="flex justify-end">
                              <button
                                onClick={() => {
                                  setCustomFieldSelections({});
                                  const newSearchParams = new URLSearchParams(searchParams);
                                  // Remove all cf_ params
                                  const keysToDelete: string[] = [];
                                  newSearchParams.forEach((_val, key) => {
                                    if (key.startsWith('cf_')) keysToDelete.push(key);
                                  });
                                  keysToDelete.forEach((key) => newSearchParams.delete(key));
                                  setSearchParams(newSearchParams);
                                  resetPagination();
                                }}
                                className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400 hover:text-red-500 transition-colors duration-200"
                              >
                                Clear custom field filters
                              </button>
                            </div>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  )}
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Collapsible Summary Section - Hidden */}
        {showHiddenSummary && <Card variant="default" className="w-full">
          <CardBody>
            <button
              className="flex items-center justify-between w-full text-left focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2"
              onClick={() => setIsSummaryCollapsed(!isSummaryCollapsed)}
              aria-expanded={!isSummaryCollapsed}
              aria-controls="summary-content"
            >
              <div className="flex items-center space-x-3">
                <Icon name="HiChartBar" className="h-5 w-5 text-xtnd-dark dark:text-xtnd-light" />
                <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
                  Summary
                </h3>
                <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400">
                  • {groupedInvoices.flatMap(g => g.transactions).length} transactions
                </span>
              </div>
              <Icon
                name={isSummaryCollapsed ? 'HiChevronDown' : 'HiChevronUp'}
                className="h-5 w-5 text-xtnd-dark-400 dark:text-xtnd-dark-500 transition-transform duration-200"
              />
            </button>

            {!isSummaryCollapsed && (
              <div id="summary-content" className="mt-4">
                <div className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
                    {/* Operational Summary */}
                    <div className="space-y-4 flex flex-col h-full">
                      <h4 className="text-md font-medium text-blue-700 dark:text-blue-300 flex items-center gap-2">
                        <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                        Operational Summary
                      </h4>

                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 flex-grow">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-blue-200 dark:border-blue-700">
                              <th className="text-left py-2 font-medium text-blue-800 dark:text-blue-200">
                                Status
                              </th>
                              <th className="text-right py-2 font-medium text-blue-800 dark:text-blue-200">
                                Sum
                              </th>
                              <th className="text-right py-2 font-medium text-blue-800 dark:text-blue-200">
                                Count
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-blue-100 dark:border-blue-800">
                              <td className="py-2 text-blue-700 dark:text-blue-300 font-medium">
                                Total
                              </td>
                              <td className="py-2 text-right font-mono text-blue-800 dark:text-blue-200">
                                {numberUtil.formatCurrency(
                                  transactionSummary.operational.total.sum
                                )}
                              </td>
                              <td className="py-2 text-right font-mono text-blue-800 dark:text-blue-200">
                                {transactionSummary.operational.total.count}
                              </td>
                            </tr>
                            <tr className="border-b border-blue-100 dark:border-blue-800">
                              <td className="py-2 text-green-700 dark:text-green-300 font-medium">
                                Paid
                              </td>
                              <td className="py-2 text-right font-mono text-green-800 dark:text-green-200">
                                {numberUtil.formatCurrency(transactionSummary.operational.paid.sum)}
                              </td>
                              <td className="py-2 text-right font-mono text-green-800 dark:text-green-200">
                                {transactionSummary.operational.paid.count}
                              </td>
                            </tr>
                            <tr>
                              <td className="py-2 text-green-700 dark:text-green-300 font-medium">
                                Balance
                              </td>
                              <td className="py-2 text-right font-mono text-green-800 dark:text-green-200">
                                {numberUtil.formatCurrency(
                                  transactionSummary.operational.balance.sum
                                )}
                              </td>
                              <td className="py-2 text-right font-mono text-green-800 dark:text-green-200">
                                {transactionSummary.operational.balance.count}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Trust Request Summary */}
                    <div className="space-y-4 flex flex-col h-full">
                      <h4 className="text-md font-medium text-green-700 dark:text-green-300 flex items-center gap-2">
                        <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                        Trust Request Summary
                      </h4>

                      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 flex-grow">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-green-200 dark:border-green-700">
                              <th className="text-left py-2 font-medium text-green-800 dark:text-green-200">
                                Status
                              </th>
                              <th className="text-right py-2 font-medium text-green-800 dark:text-green-200">
                                Sum
                              </th>
                              <th className="text-right py-2 font-medium text-green-800 dark:text-green-200">
                                Count
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-green-100 dark:border-green-800">
                              <td className="py-2 text-green-700 dark:text-green-300 font-medium">
                                Total
                              </td>
                              <td className="py-2 text-right font-mono text-green-800 dark:text-green-200">
                                {numberUtil.formatCurrency(
                                  transactionSummary.trustRequest.total.sum
                                )}
                              </td>
                              <td className="py-2 text-right font-mono text-green-800 dark:text-green-200">
                                {transactionSummary.trustRequest.total.count}
                              </td>
                            </tr>
                            <tr className="border-b border-green-100 dark:border-green-800">
                              <td className="py-2 text-yellow-700 dark:text-yellow-300 font-medium">
                                Pending
                              </td>
                              <td className="py-2 text-right font-mono text-yellow-800 dark:text-yellow-200">
                                {numberUtil.formatCurrency(
                                  transactionSummary.trustRequest.pending.sum
                                )}
                              </td>
                              <td className="py-2 text-right font-mono text-yellow-800 dark:text-yellow-200">
                                {transactionSummary.trustRequest.pending.count}
                              </td>
                            </tr>
                            <tr className="border-b border-green-100 dark:border-green-800">
                              <td className="py-2 text-blue-700 dark:text-blue-300 font-medium">
                                Paid
                              </td>
                              <td className="py-2 text-right font-mono text-blue-800 dark:text-blue-200">
                                {numberUtil.formatCurrency(
                                  transactionSummary.trustRequest.paid.sum
                                )}
                              </td>
                              <td className="py-2 text-right font-mono text-blue-800 dark:text-blue-200">
                                {transactionSummary.trustRequest.paid.count}
                              </td>
                            </tr>
                            <tr>
                              <td className="py-2 text-green-700 dark:text-green-300 font-medium">
                                Balance
                              </td>
                              <td className="py-2 text-right font-mono text-green-800 dark:text-green-200">
                                {numberUtil.formatCurrency(
                                  transactionSummary.trustRequest.balance.sum
                                )}
                              </td>
                              <td className="py-2 text-right font-mono text-green-800 dark:text-green-200">
                                {transactionSummary.trustRequest.balance.count}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Status Counts Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-grow items-stretch">
                      {/* Operational Status Counts */}
                      <div className="space-y-4 flex flex-col">
                        <h4 className="text-md font-medium text-blue-700 dark:text-blue-300 flex items-center gap-2">
                          <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                          Operational Status
                        </h4>

                        <div className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700 rounded-lg p-4 flex-grow">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-xtnd-dark-200 dark:border-xtnd-dark-500">
                                <th className="text-left py-2 font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200">
                                  Status
                                </th>
                                <th className="text-right py-2 font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200">
                                  Count
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(transactionSummary.statusCounts.operational)
                                .filter(([, count]) => count > 0)
                                .sort(([a], [b]) => a.localeCompare(b))
                                .map(([status, count]) => (
                                  <tr
                                    key={`operational-${status}`}
                                    className="border-b border-xtnd-dark-100 dark:border-xtnd-dark-600"
                                  >
                                    <td className="py-2 font-medium capitalize">
                                      <span className={`${getStatusCountTextColor(status)}`}>
                                        {status.replace(/_/g, ' ')}
                                      </span>
                                    </td>
                                    <td className="py-2 text-right font-mono text-xtnd-dark-700 dark:text-xtnd-dark-100">
                                      {count}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Trust Request Status Counts */}
                      <div className="space-y-4 flex flex-col">
                        <h4 className="text-md font-medium text-green-700 dark:text-green-300 flex items-center gap-2">
                          <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                          Trust Request Status
                        </h4>

                        <div className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700 rounded-lg p-4 flex-grow">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-xtnd-dark-200 dark:border-xtnd-dark-500">
                                <th className="text-left py-2 font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200">
                                  Status
                                </th>
                                <th className="text-right py-2 font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200">
                                  Count
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(transactionSummary.statusCounts.trustRequest)
                                .filter(([, count]) => count > 0)
                                .sort(([a], [b]) => a.localeCompare(b))
                                .map(([status, count]) => (
                                  <tr
                                    key={`trust-${status}`}
                                    className="border-b border-xtnd-dark-100 dark:border-xtnd-dark-600"
                                  >
                                    <td className="py-2 font-medium capitalize">
                                      <span className={`${getStatusCountTextColor(status)}`}>
                                        {status.replace(/_/g, ' ')}
                                      </span>
                                    </td>
                                    <td className="py-2 text-right font-mono text-xtnd-dark-700 dark:text-xtnd-dark-100">
                                      {count}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardBody>
        </Card>}

        {/* Main Transactions Table + Drawer */}
        <div id="juniper-transactions-table-container" className="w-full max-w-full flex">
          <Card id="juniper-transactions-table-card" variant="default" className="flex-1 min-w-0 shadow-sm">
            <CardHeader className="border-b border-xtnd-dark-100 dark:border-xtnd-dark-600 bg-white dark:bg-xtnd-dark-800">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center space-x-4">
                  <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-light">
                    Transactions
                  </h2>
                  {selection.selectedCount > 0 && (
                    <div className="flex items-center space-x-2 px-3 py-1.5 bg-xtnd-blue-50 dark:bg-xtnd-blue-900/20 rounded-full">
                      <span className="text-sm font-medium text-xtnd-blue dark:text-xtnd-blue-300">
                        {selection.mode === 'all'
                          ? `All ${selection.selectedCount} matching selected`
                          : `${selection.selectedCount} selected`}
                      </span>
                      <button
                        type="button"
                        onClick={() => selection.clear()}
                        className="text-xs text-xtnd-blue dark:text-xtnd-blue-300 underline hover:opacity-80"
                      >
                        clear
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsColumnModalOpen(true)}
                    className="flex items-center space-x-2 hover:bg-xtnd-dark-50 dark:hover:bg-xtnd-dark-600"
                    title="Edit columns"
                  >
                    <Icon name="HiViewGrid" className="h-4 w-4" />
                    <span className="hidden sm:inline">Edit Columns</span>
                    <span className="inline-flex items-center justify-center text-xs bg-xtnd-dark-50 dark:bg-xtnd-dark-600 text-xtnd-dark-500 dark:text-xtnd-dark-200 px-1.5 py-0.5 rounded min-w-[2rem]">
                      {getVisibleColumnCount()}/{getTotalColumnCount()}
                    </span>
                  </Button>
                  {selection.selectedCount > 0 &&
                    (() => {
                      // In 'specific' mode we have full transaction objects for every selected
                      // row (including ones on other pages). In 'all' mode we only know the count
                      // of matching records; the backend handles per-item eligibility at execution.
                      const isAllMode = selection.mode === 'all';
                      const specificObjects = selection.getSpecificObjects();
                      const countForAction = (predicate: (inv: TransformedInvoice) => boolean) =>
                        isAllMode ? selection.selectedCount : specificObjects.filter(predicate).length;

                      const eligibleEmailCount = isAllMode
                        ? 0
                        : specificObjects.filter(inv => isResendEligible(inv) && hasValidRecipients(inv)).length;

                      const eligibleRefreshCount = selection.selectedCount;
                      const eligiblePdfCount = countForAction(isPdfRegenerationEligible);
                      const eligibleConsolidateCount = isAllMode ? 0 : specificObjects.filter(isConsolidatePrintEligible).length;
                      const eligibleMarkCompletedCount = isAllMode ? 0 : specificObjects.filter(isMarkCompletedEligible).length;

                      const eligibleVoidDeleteTransactions = isAllMode ? [] : specificObjects.filter(isVoidDeleteEligible);
                      const eligibleVoidDeleteCount = eligibleVoidDeleteTransactions.length;
                      const deleteCount = eligibleVoidDeleteTransactions.filter(inv => {
                        const s = inv.trustRequest?.state?.toLowerCase();
                        return s === 'draft' || s === 'awaiting_approval';
                      }).length;
                      const voidCount = eligibleVoidDeleteTransactions.filter(inv =>
                        inv.trustRequest?.state?.toLowerCase() === 'awaiting_payment'
                      ).length;

                      const anyPending =
                        bulkSendEmails.isPending ||
                        bulkRegeneratePdf.isPending ||
                        bulkConsolidatePrint.isPending ||
                        bulkRefreshFromClio.isPending ||
                        bulkVoidDelete.isPending ||
                        bulkSimulate.isPending ||
                        bulkMarkCompleted.isPending;

                      return (
                        <div className="relative">
                          <Button
                            ref={bulkActionsButtonRef}
                            variant="primary"
                            size="sm"
                            onClick={() => setBulkActionsMenuOpen(prev => !prev)}
                            disabled={anyPending}
                            className="flex items-center space-x-2"
                          >
                            <span>Bulk Actions</span>
                            <Icon name={bulkActionsMenuOpen ? 'HiChevronUp' : 'HiChevronDown'} className="h-4 w-4" />
                          </Button>
                          <ActionsMenu
                            isOpen={bulkActionsMenuOpen}
                            onClose={() => setBulkActionsMenuOpen(false)}
                            triggerElement={bulkActionsButtonRef.current}
                          >
                            {/* Send Emails */}
                            <ActionsMenuItem
                              icon="HiMail"
                              iconColor="text-xtnd-blue"
                              label={isAllMode ? 'Send Emails (requires explicit selection)' : `Send ${eligibleEmailCount} Email${eligibleEmailCount > 1 ? 's' : ''}`}
                              disabled={isAllMode ? true : (eligibleEmailCount === 0 || bulkSendEmails.isPending)}
                              title={isAllMode
                                ? 'Email send requires per-matter-type templates and specific recipients. Use the checkboxes to pick transactions explicitly.'
                                : eligibleEmailCount === 0 ? 'No eligible transactions for email send' : `Send emails to ${eligibleEmailCount} eligible transaction(s)`}
                              onClick={() => {
                                setBulkActionsMenuOpen(false);
                                handleBulkSend();
                              }}
                            />

                            {/* Regenerate PDF */}
                            <ActionsMenuItem
                              icon="HiRefresh"
                              iconColor="text-orange-500"
                              label={isAllMode ? `Regenerate ${selection.selectedCount} PDF${selection.selectedCount > 1 ? 's' : ''}` : `Regenerate ${eligiblePdfCount} PDF${eligiblePdfCount > 1 ? 's' : ''}`}
                              disabled={(isAllMode ? selection.selectedCount === 0 : eligiblePdfCount === 0) || bulkRegeneratePdf.isPending}
                              title={isAllMode
                                ? `Regenerate PDFs for up to ${selection.selectedCount} matching transaction(s); ineligible items will be skipped.`
                                : eligiblePdfCount === 0 ? 'No eligible transactions for PDF regeneration' : `Regenerate PDFs for ${eligiblePdfCount} transaction(s)`}
                              onClick={() => {
                                setBulkActionsMenuOpen(false);
                                handleBulkRegeneratePdf();
                              }}
                            />

                            <ActionsMenuItem
                              icon="HiPrinter"
                              iconColor="text-xtnd-blue"
                              label={isAllMode ? `Consolidate & Print (requires explicit selection)` : `Consolidate & Print ${eligibleConsolidateCount} PDF${eligibleConsolidateCount > 1 ? 's' : ''}`}
                              disabled={isAllMode ? true : (eligibleConsolidateCount === 0 || bulkConsolidatePrint.isPending)}
                              title={isAllMode
                                ? `Consolidate print is capped at ${MAX_CONSOLIDATE_PRINT} items. Use the checkboxes to pick specific transactions.`
                                : eligibleConsolidateCount === 0 ? 'No eligible Email Held transactions with stored invoice PDFs' : `Create one printable PDF from ${eligibleConsolidateCount} transaction(s)`}
                              onClick={() => {
                                setBulkActionsMenuOpen(false);
                                handleBulkConsolidatePrint();
                              }}
                            />

                            {/* Mark Completed */}
                            <ActionsMenuItem
                              icon="HiCheckCircle"
                              iconColor="text-green-500"
                              label={`Mark Completed ${!isAllMode && eligibleMarkCompletedCount > 0 ? `(${eligibleMarkCompletedCount})` : ''}`}
                              disabled={(isAllMode ? false : eligibleMarkCompletedCount === 0) || bulkMarkCompleted.isPending}
                              title={isAllMode
                                ? 'Mark all matching Email Held transactions as completed'
                                : eligibleMarkCompletedCount === 0 ? 'No Email Held transactions selected' : `Mark ${eligibleMarkCompletedCount} transaction(s) as completed without sending email`}
                              onClick={async () => {
                                setBulkActionsMenuOpen(false);
                                if (isAllMode) {
                                  showToast.warning(
                                    'Mark completed requires explicit selection. Use the checkboxes to pick the Email Held transactions you want to mark completed.'
                                  );
                                  return;
                                }
                                const ids = specificObjects
                                  .filter(isMarkCompletedEligible)
                                  .map(inv => inv.id)
                                  .filter((id): id is string => !!id);
                                if (ids.length === 0) {
                                  showToast.warning('No eligible transactions for mark completed');
                                  return;
                                }
                                const requestId = `bulk-mark-completed-${Date.now()}-${Math.random().toString(36).slice(2)}`;
                                bulkMarkCompleted.mutate(
                                  { juniperIds: ids, requestId, reason: 'Bulk mark completed from juniper transactions page' },
                                  {
                                    onSuccess: (resp: any) => {
                                      selection.clear();
                                      trackBulkJob(resp);
                                    },
                                  },
                                );
                              }}
                            />

                            {/* Refresh from Clio */}
                            <ActionsMenuItem
                              icon="HiRefresh"
                              iconColor="text-xtnd-blue"
                              label={`Refresh ${eligibleRefreshCount} From Clio`}
                              disabled={bulkRefreshFromClio.isPending}
                              onClick={() => {
                                setBulkActionsMenuOpen(false);
                                handleBulkRefreshFromClio();
                              }}
                            />

                            <ActionsMenuDivider />

                            {/* Simulate Events */}
                            <ActionsMenuItem
                              icon="HiLightningBolt"
                              iconColor="text-yellow-500"
                              label={`Retry ${selection.selectedCount} Last Event${selection.selectedCount > 1 ? 's' : ''}`}
                              disabled={bulkSimulate.isPending}
                              onClick={() => {
                                setBulkActionsMenuOpen(false);
                                setBulkSimulateModalOpen(true);
                              }}
                            />

                            {/* Void/Delete Trust Requests */}
                            {eligibleVoidDeleteCount > 0 && (
                              <>
                                <ActionsMenuDivider />
                                <ActionsMenuItem
                                  icon="HiTrash"
                                  iconColor="text-red-500"
                                  label={`${deleteCount > 0 ? `Delete ${deleteCount}` : ''}${deleteCount > 0 && voidCount > 0 ? ' / ' : ''}${voidCount > 0 ? `Void ${voidCount}` : ''} Trust Request${eligibleVoidDeleteCount > 1 ? 's' : ''}`}
                                  disabled={bulkVoidDelete.isPending}
                                  title={`Void or delete ${eligibleVoidDeleteCount} trust request(s)`}
                                  onClick={() => {
                                    setBulkActionsMenuOpen(false);
                                    handleBulkVoidDelete();
                                  }}
                                />
                              </>
                            )}
                          </ActionsMenu>
                        </div>
                      );
                    })()}
                </div>
              </div>
            </CardHeader>
            <CardBody className="p-0">
              {(() => {
                // "Select all N matching" banner: shown when every visible row is
                // selected AND there are more matching records than the current page.
                const visibleTransactions =
                  groupedInvoices?.flatMap(group => group.transactions) || [];
                // Banner hidden for now — the 'all matching' cross-page flow isn't
                // ready to ship yet (backend /transactions/ids wiring + bulk-action
                // eligibility gating still being worked out). To re-enable, restore:
                //   const allVisibleSelected = visibleTransactions.length > 0 &&
                //     visibleTransactions.every(inv => selection.isSelected(inv.id));
                //   const showOfferBanner = selection.mode === 'specific' &&
                //     allVisibleSelected && totalRecords > visibleTransactions.length;
                const showOfferBanner = false;

                if (selection.mode === 'all') {
                  return (
                    <div className="px-6 py-2 bg-xtnd-blue-50 dark:bg-xtnd-blue-900/20 border-b border-xtnd-blue-200 dark:border-xtnd-blue-800 text-sm text-xtnd-blue-900 dark:text-xtnd-blue-200 flex items-center justify-between">
                      <span>
                        All <strong>{selection.selectedCount}</strong> transactions matching the current filters are selected
                        {selection.excludedIds.size > 0 ? ` (${selection.excludedIds.size} excluded)` : ''}.
                      </span>
                      <button
                        type="button"
                        onClick={() => selection.clear()}
                        className="text-xtnd-blue-700 dark:text-xtnd-blue-300 underline text-sm hover:opacity-80"
                      >
                        Clear selection
                      </button>
                    </div>
                  );
                }

                if (showOfferBanner) {
                  return (
                    <div className="relative px-6 py-3 bg-xtnd-blue-50 dark:bg-xtnd-blue-900/30 border-b-2 border-xtnd-blue/40 dark:border-xtnd-blue-700 text-sm text-xtnd-blue-900 dark:text-xtnd-blue-100 flex items-center justify-between animate-slide-up">
                      <span className="flex items-center space-x-2">
                        <Icon name="HiInformationCircle" className="h-5 w-5 text-xtnd-blue" />
                        <span>
                          All <strong>{visibleTransactions.length}</strong> on this page are selected.
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          dismissSelectAllHint();
                          handleSelectAllMatching();
                        }}
                        className="inline-flex items-center space-x-1 bg-xtnd-blue hover:bg-xtnd-blue-700 text-white text-sm font-semibold px-3 py-1.5 rounded shadow-sm transition-colors"
                      >
                        <span>Select all {totalRecords.toLocaleString()} across all pages</span>
                        <Icon name="HiArrowRight" className="h-4 w-4" />
                      </button>
                      {!selectAllHintDismissed && (
                        <div className="absolute top-full right-6 mt-3 z-50 w-80 bg-xtnd-dark-800 dark:bg-xtnd-dark-900 text-white rounded-lg shadow-2xl p-4 animate-fade-in">
                          {/* Arrow */}
                          <div className="absolute -top-1.5 right-8 w-3 h-3 bg-xtnd-dark-800 dark:bg-xtnd-dark-900 rotate-45" />
                          <div className="relative flex items-start space-x-2">
                            <Icon name="HiLightBulb" className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <div className="text-sm font-semibold mb-1">Tip: Act on all matching records</div>
                              <p className="text-xs text-gray-200 mb-3 leading-relaxed">
                                Click the blue button to run bulk actions on every record matching your filters — not just this page. You can deselect individual rows from any page before taking action.
                              </p>
                              <button
                                type="button"
                                onClick={dismissSelectAllHint}
                                className="text-xs bg-xtnd-blue hover:bg-xtnd-blue-700 text-white px-3 py-1 rounded font-medium"
                              >
                                Got it
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                return null;
              })()}
              <div className="overflow-x-auto overflow-y-auto relative juniper-transactions-scroll max-h-[calc(100vh-300px)] min-h-[700px]">
                <table
                  className="min-w-full divide-y divide-xtnd-dark-100 dark:divide-xtnd-dark-600 relative"
                  style={{
                    '--pin-checkbox': '0px',
                    '--pin-indicators': '5rem',   /* checkbox col ~80px = 5rem */
                    '--pin-matter': '7.5rem',      /* 5rem + w-10(2.5rem) = 7.5rem */
                    '--pin-shadow': '4px 0 8px -1px rgba(0,0,0,0.15)',
                  } as React.CSSProperties}
                >
                  <thead className="bg-white dark:bg-xtnd-dark-700 sticky top-0 z-30">
                    {/* Section Header Row */}
                    <tr className="border-b-2 border-xtnd-dark-200 dark:border-xtnd-dark-500">
                      <th className="px-4 py-3 w-20 min-w-[5rem] bg-xtnd-dark-50 dark:bg-xtnd-dark-600 sticky left-[var(--pin-checkbox)] z-20"></th>
                      <th className="px-1 py-3 w-10 min-w-[2.5rem] bg-xtnd-dark-50 dark:bg-xtnd-dark-600 sticky left-[var(--pin-indicators)] z-20"></th>
                      <th className="px-6 py-3 text-xs font-bold text-xtnd-dark-600 dark:text-xtnd-dark-100 uppercase tracking-wider text-left bg-xtnd-dark-50 dark:bg-xtnd-dark-600 sticky left-[var(--pin-matter)] z-20 shadow-[var(--pin-shadow)]">
                        Basic Information
                      </th>
                      <th
                        colSpan={getHeaderSpans().matterInfo - 2}
                        className="px-6 py-3 text-xs font-bold text-xtnd-dark-600 dark:text-xtnd-dark-100 uppercase tracking-wider text-left bg-xtnd-dark-50 dark:bg-xtnd-dark-600 border-r-2 border-xtnd-dark-200 dark:border-xtnd-dark-500"
                      >
                      </th>
                      <th
                        colSpan={getHeaderSpans().operationalBill}
                        className="px-6 py-3 text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wider text-left bg-blue-100 dark:bg-blue-900/30 border-r-2 border-xtnd-dark-200 dark:border-xtnd-dark-500"
                      >
                        Operational Bill
                      </th>
                      <th
                        colSpan={getHeaderSpans().trustRequestBill}
                        className="px-6 py-3 text-xs font-bold text-green-700 dark:text-green-300 uppercase tracking-wider text-left bg-green-100 dark:bg-green-900/30 border-r-2 border-xtnd-dark-200 dark:border-xtnd-dark-500"
                      >
                        Trust Request Bill
                      </th>
                      <th
                        colSpan={getHeaderSpans().actions}
                        className="py-3 px-3 text-xs font-bold text-xtnd-dark-600 dark:text-xtnd-dark-100 uppercase tracking-wider text-center bg-xtnd-dark-50 dark:bg-xtnd-dark-600 w-16 sticky right-0 z-40 shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.1)]"
                      >
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>

                    {/* Column Headers Row */}
                    <tr className="bg-white dark:bg-xtnd-dark-700 border-b border-xtnd-dark-100 dark:border-xtnd-dark-600">
                      {/* Checkbox Column - Sticky Left */}
                      <th className="px-4 py-3 w-20 min-w-[5rem] bg-xtnd-dark-50 dark:bg-xtnd-dark-700 sticky left-[var(--pin-checkbox)] z-20">
                        <div className="flex items-center justify-center space-x-2">
                          <input
                            type="checkbox"
                            ref={(el) => {
                              // Indeterminate = some visible rows selected but not all.
                              if (!el || !groupedInvoices) return;
                              const visible = groupedInvoices.flatMap(g => g.transactions);
                              const selectedVisible = visible.filter(inv => selection.isSelected(inv.id)).length;
                              el.indeterminate = selectedVisible > 0 && selectedVisible < visible.length;
                            }}
                            checked={
                              !!groupedInvoices &&
                              groupedInvoices.length > 0 &&
                              (() => {
                                const visible = groupedInvoices.flatMap(g => g.transactions);
                                return visible.length > 0 && visible.every(inv => selection.isSelected(inv.id));
                              })()
                            }
                            onChange={handleSelectAll}
                            className="h-4 w-4 text-xtnd-blue border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded focus:ring-2 focus:ring-xtnd-blue cursor-pointer"
                            title={
                              groupedInvoices && groupedInvoices.length > 0
                                ? (() => {
                                    const visibleCount = groupedInvoices.flatMap(g => g.transactions).length;
                                    return visibleCount === 0
                                      ? 'No transactions in current view'
                                      : `Select all ${visibleCount} transaction${visibleCount > 1 ? 's' : ''} on this page`;
                                  })()
                                : 'No transactions in current view'
                            }
                            disabled={
                              !groupedInvoices ||
                              groupedInvoices.length === 0 ||
                              groupedInvoices.flatMap(g => g.transactions).length === 0
                            }
                          />
                          <button
                            onClick={() => {
                              refetchInvoices();
                            }}
                            disabled={isFetchingInvoices}
                            className="p-1 text-xtnd-dark-300 hover:text-xtnd-blue disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded"
                            title="Refresh transactions and status counts"
                          >
                            <Icon
                              name="HiRefresh"
                              className={`h-4 w-4 ${isFetchingInvoices ? 'animate-spin' : ''}`}
                            />
                          </button>
                        </div>
                      </th>
                      {/* Indicators Column Header - Sticky Left */}
                      <th className="px-1 py-3 w-10 min-w-[2.5rem] text-center bg-xtnd-dark-50 dark:bg-xtnd-dark-700 sticky left-[var(--pin-indicators)] z-20">
                        <Icon
                          name="HiInformationCircle"
                          className="h-4 w-4 text-xtnd-dark-400 dark:text-xtnd-dark-300 mx-auto"
                          title="Indicators (Warnings & Receipts)"
                        />
                      </th>
                      {/* Matter Information - Sticky Left */}
                      <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-300 uppercase tracking-wider bg-xtnd-dark-50 dark:bg-xtnd-dark-700 sticky left-[var(--pin-matter)] z-20 shadow-[var(--pin-shadow)]">
                        Matter
                      </th>
                      {visibleColumns.matterType && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-300 uppercase tracking-wider bg-xtnd-dark-50 dark:bg-xtnd-dark-700 min-w-[140px]">
                          Type
                        </th>
                      )}
                      {visibleColumns.clientName && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-300 uppercase tracking-wider bg-xtnd-dark-50 dark:bg-xtnd-dark-700 min-w-[200px]">
                          Client
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-300 uppercase tracking-wider bg-xtnd-dark-50 dark:bg-xtnd-dark-700 min-w-[180px]">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-300 uppercase tracking-wider bg-xtnd-dark-50 dark:bg-xtnd-dark-700 min-w-[150px]">
                        Last Sent
                      </th>
                      {visibleColumns.createdAt && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-300 uppercase tracking-wider bg-xtnd-dark-50 dark:bg-xtnd-dark-700 min-w-[150px]">
                          Created
                        </th>
                      )}
                      {visibleColumns.updatedAt && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-300 uppercase tracking-wider bg-xtnd-dark-50 dark:bg-xtnd-dark-700 min-w-[150px]">
                          Updated
                        </th>
                      )}
                      {visibleColumns.delivery && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-300 uppercase tracking-wider bg-xtnd-dark-50 dark:bg-xtnd-dark-700 min-w-[140px]">
                          Delivery
                        </th>
                      )}
                      {visibleColumns.paymentLink && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-dark-300 uppercase tracking-wider bg-xtnd-dark-50 dark:bg-xtnd-dark-700 border-r-2 border-xtnd-dark-100 dark:border-xtnd-dark-600 min-w-[200px]">
                          Payment Link
                        </th>
                      )}

                      {/* Operational Bill Columns */}
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider bg-blue-50/70 dark:bg-blue-900/20 min-w-[120px]">
                        Number
                      </th>
                      {visibleColumns.operationalBillTotal && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider bg-blue-50/70 dark:bg-blue-900/20 min-w-[120px]">
                          Total
                        </th>
                      )}
                      {visibleColumns.operationalBillPaid && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider bg-blue-50/70 dark:bg-blue-900/20 min-w-[120px]">
                          Paid
                        </th>
                      )}
                      {visibleColumns.operationalBillBalance && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider bg-blue-50/70 dark:bg-blue-900/20 min-w-[120px]">
                          Balance
                        </th>
                      )}
                      {visibleColumns.operationalBillDue && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider bg-blue-50/70 dark:bg-blue-900/20 min-w-[120px]">
                          Due Amount
                        </th>
                      )}
                      {visibleColumns.operationalBillIssuedAt && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider bg-blue-50/70 dark:bg-blue-900/20 min-w-[120px]">
                          Issued
                        </th>
                      )}
                      {visibleColumns.operationalBillDueAt && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider bg-blue-50/70 dark:bg-blue-900/20 min-w-[120px]">
                          Due Date
                        </th>
                      )}
                      {visibleColumns.lastOperationalPaymentAt && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider bg-blue-50/70 dark:bg-blue-900/20 min-w-[150px]">
                          Last Payment
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider bg-blue-50/70 dark:bg-blue-900/20 border-r-2 border-xtnd-dark-100 dark:border-xtnd-dark-600 min-w-[200px]">
                        Status
                      </th>

                      {/* Trust Request Bill Columns */}
                      <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[120px]">
                        TRR Number
                      </th>
                      {visibleColumns.trustRequestBillTotal && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[120px]">
                          Total
                        </th>
                      )}
                      {visibleColumns.trustRequestBillPaid && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[120px]">
                          Paid
                        </th>
                      )}
                      {visibleColumns.trustRequestBillBalance && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[120px]">
                          Balance
                        </th>
                      )}
                      {visibleColumns.trustRequestBillDue && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[120px]">
                          Due Amount
                        </th>
                      )}
                      {visibleColumns.trustRequestBillPending && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[120px]">
                          Pending
                        </th>
                      )}
                      {visibleColumns.trustRequestBillIssuedAt && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[120px]">
                          Issued
                        </th>
                      )}
                      {visibleColumns.trustRequestBillDueAt && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[120px]">
                          Due Date
                        </th>
                      )}
                      {visibleColumns.lastTrustRequestPaymentAt && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[150px]">
                          Last Payment
                        </th>
                      )}
                      {visibleColumns.trustRequestEmailType && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[160px]">
                          Email Type
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 min-w-[200px]">
                        Status
                      </th>
                      {visibleColumns.trustRequestCalculation && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider bg-green-50/70 dark:bg-green-900/20 border-r-2 border-xtnd-dark-100 dark:border-xtnd-dark-600 min-w-[240px]">
                          Calculation
                        </th>
                      )}

                      {/* Actions - Sticky Right */}
                      <th className="py-3 px-3 text-center bg-white dark:bg-xtnd-dark-700 w-16 sticky right-0 z-40 shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.1)]">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-xtnd-dark-700 divide-y divide-xtnd-dark-100 dark:divide-xtnd-dark-600">
                    {isLoadingInvoices ? (
                      <tr>
                        <td
                          colSpan={getHeaderSpans().total}
                          className="px-6 py-8 text-center text-xtnd-dark-500 dark:text-xtnd-dark-400"
                        >
                          Loading transactions...
                        </td>
                      </tr>
                    ) : invoicesError ? (
                      <tr>
                        <td
                          colSpan={getHeaderSpans().total}
                          className="px-6 py-8 text-center text-red-500 dark:text-red-400"
                        >
                          Error loading transactions: {invoicesError.message}
                        </td>
                      </tr>
                    ) : groupedInvoices.reduce(
                        (total, group) => total + group.transactions.length,
                        0
                      ) === 0 ? (
                      <tr>
                        <td
                          colSpan={getHeaderSpans().total}
                          className="px-6 py-12 text-center"
                        >
                          <div className="flex flex-col items-center justify-center space-y-4">
                            <Icon name="HiFilter" className="h-12 w-12 text-xtnd-dark-300 dark:text-xtnd-dark-600" />
                            {activeStatusFilters.length === 0 ? (
                              <>
                                <p className="text-lg font-medium text-xtnd-dark dark:text-xtnd-light">
                                  No status filters selected
                                </p>
                                <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-400">
                                  Please select one or more status cards above to view transactions
                                </p>
                                <Button
                                  variant="primary"
                                  size="md"
                                  onClick={() => {
                                    setActiveStatusFilters(DEFAULT_ACTIVE_FILTERS);
                                    const newSearchParams = new URLSearchParams(searchParams);
                                    newSearchParams.set('statusFilters', DEFAULT_ACTIVE_FILTERS.join(','));
                                    setSearchParams(newSearchParams);
                                    resetPagination();
                                  }}
                                  leftIcon={<Icon name="HiRefresh" className="h-5 w-5" />}
                                >
                                  Load Default Filters
                                </Button>
                              </>
                            ) : (
                              <>
                                <p className="text-lg font-medium text-xtnd-dark dark:text-xtnd-light">
                                  No transactions found
                                </p>
                                <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-400">
                                  No transactions match the selected filters for this date range
                                </p>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      groupedInvoices.map(group => (
                        <React.Fragment key={group.period}>
                          {/* Bill Run Header */}
                          <tr>
                            <td
                              colSpan={getHeaderSpans().total}
                              className="p-0 bg-xtnd-blue-50 dark:bg-xtnd-dark-700 border-t-2 border-xtnd-blue-200 dark:border-xtnd-dark-600"
                            >
                              <div className="sticky left-0 w-fit px-6 py-3">
                                <div className="flex items-center space-x-2">
                                  <div className="w-3 h-3 rounded-full bg-xtnd-blue flex-shrink-0"></div>
                                  <h3 className="text-sm font-semibold text-xtnd-dark dark:text-xtnd-light whitespace-nowrap">
                                    {group.period}
                                  </h3>
                                  <span className="text-xs text-xtnd-dark-600 dark:text-xtnd-dark-400 whitespace-nowrap">
                                    ({group.transactions.length} transactions)
                                  </span>
                                </div>
                              </div>
                            </td>
                          </tr>

                          {/* Transactions in this group */}
                          {group.transactions.map(invoice => {
                            const isSelected = selection.isSelected(invoice.id);
                            const isActive = activeRowId === invoice.id;
                            // Row is locked while a bulk action is enrolled on it. The 'failed'
                            // sticky state intentionally does NOT lock — users need to retry
                            // failed rows manually.
                            const processingLocked =
                              !!invoice.processingAction && invoice.processingState !== 'failed';
                            // Row state classes: normal < hover < selected (checkbox) < active (clicked) < selected+active
                            const rowStateClass = [
                              'group transition-colors cursor-pointer relative',
                              isSelected && !isActive
                                ? 'bg-teal-50 dark:bg-teal-900'
                                : '',
                              isActive && !isSelected
                                ? 'bg-xtnd-dark-100 dark:bg-xtnd-dark-600'
                                : '',
                              isActive && isSelected
                                ? 'bg-teal-100 dark:bg-teal-800'
                                : '',
                              !isSelected && !isActive
                                ? 'hover:bg-xtnd-dark-50 dark:hover:bg-xtnd-dark-600/50'
                                : isSelected
                                  ? 'hover:bg-teal-100 dark:hover:bg-teal-800'
                                  : 'hover:bg-xtnd-dark-200/70 dark:hover:bg-xtnd-dark-600/70',
                            ].filter(Boolean).join(' ');
                            return (
                            <tr
                              key={invoice.id}
                              className={rowStateClass}
                              onClick={() => handleRowClick(invoice.id)}
                              onDoubleClick={() => handleRowDoubleClick(invoice.id)}
                            >
                              {/* Checkbox Column - Sticky Left with accent bar */}
                              <td
                                className={`px-4 py-4 text-center w-20 min-w-[5rem] sticky left-[var(--pin-checkbox)] z-10 transition-colors ${
                                  isSelected
                                    ? 'border-l-[3px] border-l-teal-500 bg-teal-50 dark:bg-teal-900'
                                    : isActive
                                      ? 'border-l-[3px] border-l-xtnd-dark-300 dark:border-l-xtnd-dark-400 bg-xtnd-dark-100 dark:bg-xtnd-dark-600'
                                      : 'border-l-[3px] border-l-transparent bg-white dark:bg-xtnd-dark-700 group-hover:bg-xtnd-dark-100 dark:group-hover:bg-xtnd-dark-600'
                                }`}
                                onClick={e => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleToggleSelection(invoice)}
                                  disabled={!!invoice.processingAction && invoice.processingState !== 'failed'}
                                  className="h-4 w-4 text-xtnd-blue border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded focus:ring-2 focus:ring-xtnd-blue cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                                  title={
                                    invoice.processingAction && invoice.processingState !== 'failed'
                                      ? 'Locked — bulk action in flight'
                                      : 'Select transaction for bulk actions'
                                  }
                                />
                              </td>
                              {/* Indicators Column - Sticky Left */}
                              <td
                                className={`px-1 py-4 text-center w-10 min-w-[2.5rem] sticky left-[var(--pin-indicators)] z-10 transition-colors ${
                                  isSelected
                                    ? 'bg-teal-50 dark:bg-teal-900'
                                    : isActive
                                      ? 'bg-xtnd-dark-100 dark:bg-xtnd-dark-600'
                                      : 'bg-white dark:bg-xtnd-dark-700 group-hover:bg-xtnd-dark-100 dark:group-hover:bg-xtnd-dark-600'
                                }`}
                              >
                                <div className="flex items-center justify-center space-x-1.5">
                                  {/* Warning Indicator */}
                                  <WarningsIndicator warnings={getTransactionWarnings(invoice)} />

                                  {/* Receipt Count Indicator */}
                                  {(() => {
                                    const receiptCount =
                                      invoice.documents?.storedAssets?.receipts?.length || 0;
                                    return receiptCount > 0 ? (
                                      <div
                                        className="flex items-center space-x-0.5 text-green-600 dark:text-green-400 cursor-help"
                                        title={`${receiptCount} receipt${receiptCount > 1 ? 's' : ''} attached`}
                                      >
                                        <Icon name="HiDocumentText" className="h-3.5 w-3.5" />
                                        <span className="text-xs font-semibold">
                                          {receiptCount}
                                        </span>
                                      </div>
                                    ) : null;
                                  })()}
                                </div>
                              </td>
                              {/* Matter Information - Sticky Left */}
                              <td
                                className={`px-6 py-4 text-sm sticky left-[var(--pin-matter)] z-10 shadow-[var(--pin-shadow)] transition-colors ${
                                  isSelected
                                    ? 'bg-teal-50 dark:bg-teal-900'
                                    : isActive
                                      ? 'bg-xtnd-dark-100 dark:bg-xtnd-dark-600'
                                      : 'bg-white dark:bg-xtnd-dark-700 group-hover:bg-xtnd-dark-100 dark:group-hover:bg-xtnd-dark-600'
                                }`}
                              >
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => {
                                      window.open(
                                        `https://app.clio.com/matters/${invoice.matterId}`,
                                        'clio-tab'
                                      );
                                    }}
                                    className="flex items-center space-x-1 text-xtnd-blue hover:text-xtnd-blue-600 dark:text-xtnd-blue-300 dark:hover:text-xtnd-blue-200 hover:underline focus:outline-none focus:ring-2 focus:ring-xtnd-blue rounded transition-colors"
                                  >
                                    <span className="font-medium max-w-xs truncate">
                                      {invoice.matterName || 'Unknown Matter'}
                                    </span>
                                    {invoice.matterStatus?.toLowerCase() === 'closed' && (
                                      <Icon
                                        name="HiLockClosed"
                                        className="h-3.5 w-3.5 flex-shrink-0 text-xtnd-dark-300 dark:text-xtnd-dark-400"
                                        title="Closed Matter"
                                      />
                                    )}
                                    <Icon
                                      name="HiExternalLink"
                                      className="h-3.5 w-3.5 flex-shrink-0"
                                    />
                                  </button>
                                  {invoice.matterId && (
                                    <Button
                                      onClick={() =>
                                        copyToClipboard(invoice.matterId.toString(), 'Matter ID')
                                      }
                                      variant="ghost"
                                      size="sm"
                                      className="p-1 h-auto opacity-0 group-hover:opacity-100 transition-opacity"
                                      title="Copy Matter ID"
                                    >
                                      <Icon
                                        name="HiClipboard"
                                        className="h-3.5 w-3.5 text-xtnd-dark-300 hover:text-xtnd-dark-500 dark:text-xtnd-dark-400 dark:hover:text-xtnd-dark-200"
                                      />
                                    </Button>
                                  )}
                                </div>
                              </td>
                              {/* Matter Type */}
                              {visibleColumns.matterType && (
                                <td className="px-6 py-4 text-sm min-w-[140px]">
                                  {(() => {
                                    const matterType = invoice.matterType;
                                    const status = invoice.matterTypeStatus;
                                    const message = invoice.matterTypeMessage;
                                    const missingFields = invoice.matterTypeMissingFields;

                                    if (!matterType || status === 'error' || status === 'missing_config' || status === 'missing_field') {
                                      // Show warning indicator with tooltip
                                      return (
                                        <div className="relative group/type inline-flex items-center space-x-1.5">
                                          <Icon
                                            name="HiExclamation"
                                            className="h-4 w-4 text-amber-500"
                                            title="Type determination issue"
                                          />
                                          <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300 text-xs">
                                            {matterType || 'Unknown'}
                                          </span>
                                          {/* Tooltip on hover */}
                                          <div className="absolute left-0 top-full mt-2 hidden group-hover/type:block z-50 w-64 bg-amber-50 dark:bg-amber-900/95 border border-amber-200 dark:border-amber-700 rounded-lg shadow-xl p-3">
                                            <div className="text-xs text-left">
                                              <div className="font-semibold text-amber-800 dark:text-amber-200 mb-2 flex items-center space-x-1">
                                                <Icon name="HiExclamation" className="h-4 w-4" />
                                                <span>Type Determination Issue</span>
                                              </div>
                                              <div className="text-amber-700 dark:text-amber-300 space-y-1.5">
                                                {status === 'missing_config' && (
                                                  <p>No conditional logic configuration found for this tenant.</p>
                                                )}
                                                {status === 'missing_field' && (
                                                  <>
                                                    <p>{message || 'Missing required custom fields'}</p>
                                                    {missingFields && missingFields.length > 0 && (
                                                      <ul className="mt-1 space-y-0.5 pl-2">
                                                        {missingFields.map((field: string, idx: number) => (
                                                          <li key={idx} className="flex items-start space-x-1">
                                                            <span>•</span>
                                                            <span>{field}</span>
                                                          </li>
                                                        ))}
                                                      </ul>
                                                    )}
                                                  </>
                                                )}
                                                {status === 'error' && (
                                                  <p>{message || 'Error determining matter type'}</p>
                                                )}
                                                <div className="mt-2 pt-2 border-t border-amber-200 dark:border-amber-700/60 text-amber-800 dark:text-amber-200">
                                                  <p className="flex items-start space-x-1">
                                                    <Icon name="HiRefresh" className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                                                    <span>
                                                      Use <span className="font-semibold">Refresh from Clio</span> in the row actions menu to resync this matter from Clio.
                                                    </span>
                                                  </p>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    }

                                    // Show type with appropriate styling
                                    const isEvergreen = matterType === 'Evergreen';
                                    return (
                                      <span
                                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                          isEvergreen
                                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                            : 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                                        }`}
                                        title={`Matter routing: ${matterType}`}
                                      >
                                        {matterType}
                                      </span>
                                    );
                                  })()}
                                </td>
                              )}
                              {visibleColumns.clientName && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50 min-w-[200px]">
                                  <span className="font-medium">
                                    {invoice.client || 'Unknown Client'}
                                  </span>
                                </td>
                              )}
                              <td className="px-6 py-4 text-sm min-w-[180px]">
                                <div className="flex flex-col gap-1">
                                  <span
                                    className={`${getJuniperStatusColor(invoice.juniperStatus || '')} whitespace-nowrap`}
                                  >
                                    {invoice.juniperStatus ? stringUtil.capitalizeFirstLetter(invoice.juniperStatus) : 'Unknown'}
                                  </span>
                                  <ProcessingActionBadge
                                    action={invoice.processingAction}
                                    state={invoice.processingState}
                                    error={invoice.processingError}
                                  />
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[150px] responsive-datetime">
                                {invoice.lastSentAt ? (
                                  <div className="flex flex-col sm:block">
                                    <div className="font-medium">
                                      {formatResponsiveDateTime(invoice.lastSentAt).date}
                                    </div>
                                    <div className="text-xs text-xtnd-dark-400 sm:hidden">
                                      {formatResponsiveDateTime(invoice.lastSentAt).time}
                                    </div>
                                  </div>
                                ) : (
                                  'Not sent'
                                )}
                              </td>
                              {visibleColumns.createdAt && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[150px] responsive-datetime">
                                  {invoice.createdAt ? (
                                    <div className="flex flex-col sm:block">
                                      <div className="font-medium">
                                        {formatResponsiveDateTime(invoice.createdAt).date}
                                      </div>
                                      <div className="text-xs text-xtnd-dark-400 sm:hidden">
                                        {formatResponsiveDateTime(invoice.createdAt).time}
                                      </div>
                                    </div>
                                  ) : (
                                    ''
                                  )}
                                </td>
                              )}
                              {visibleColumns.updatedAt && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[150px] responsive-datetime">
                                  {invoice.updatedAt ? (
                                    <div className="flex flex-col sm:block">
                                      <div className="font-medium">
                                        {formatResponsiveDateTime(invoice.updatedAt).date}
                                      </div>
                                      <div className="text-xs text-xtnd-dark-400 sm:hidden">
                                        {formatResponsiveDateTime(invoice.updatedAt).time}
                                      </div>
                                    </div>
                                  ) : (
                                    ''
                                  )}
                                </td>
                              )}
                              {visibleColumns.delivery && (
                                <td className="px-6 py-4 text-sm min-w-[140px]">
                                  {(() => {
                                    const summary = deliverySummaryByJuniperId.get(invoice.id);
                                    if (!summary) {
                                      return (
                                        <span className="text-xtnd-dark-300 dark:text-xtnd-dark-400">
                                          {isLoadingDeliverySummary ? 'Loading...' : '-'}
                                        </span>
                                      );
                                    }

                                    const countLabel = `${summary.deliveredToRecipientsCount} / ${summary.totalToRecipients}`;

                                    return (
                                      <button
                                        type="button"
                                        onClick={() => openDrawerToTab(invoice.id, 'email')}
                                        className="text-left hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-xtnd-blue rounded"
                                      >
                                        <div className="font-semibold text-xtnd-dark-800 dark:text-xtnd-dark-50">
                                          {countLabel}
                                        </div>
                                        <span
                                          className={`inline-flex mt-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${getDeliveryStatusBadgeClass(summary.statusBucket)}`}
                                        >
                                          {formatDeliveryStatusBucket(summary.statusBucket)}
                                        </span>
                                      </button>
                                    );
                                  })()}
                                </td>
                              )}
                              {visibleColumns.paymentLink && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[200px] border-r-2 border-xtnd-dark-100 dark:border-xtnd-dark-500">
                                  {(() => {
                                    const hasPaymentLink =
                                      invoice.clioPaymentLinkUrl && invoice.clioPaymentLinkId;
                                    const isPaid =
                                      invoice.trustRequest?.trrStatus?.toLowerCase() === 'paid';

                                    if (!hasPaymentLink) {
                                      return (
                                        <span className="text-xtnd-dark-300 dark:text-xtnd-dark-400">
                                          No link
                                        </span>
                                      );
                                    }

                                    return (
                                      <div className="flex items-center space-x-2">
                                        <a
                                          href={invoice.clioPaymentLinkUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className={`hover:underline flex items-center space-x-1 ${
                                            isPaid
                                              ? 'text-xtnd-dark-300 dark:text-xtnd-dark-400 cursor-not-allowed opacity-60'
                                              : 'text-xtnd-blue hover:text-xtnd-blue-600 dark:text-xtnd-blue-300 dark:hover:text-xtnd-blue-200'
                                          }`}
                                          onClick={e => {
                                            if (isPaid) {
                                              e.preventDefault();
                                              showToast.warning(
                                                'Payment link is not active for paid transactions'
                                              );
                                            }
                                          }}
                                          title={
                                            isPaid
                                              ? 'Payment link not active - transaction is paid'
                                              : 'Open payment link'
                                          }
                                        >
                                          <span className="truncate max-w-[120px]">
                                            {invoice.clioPaymentLinkId}
                                          </span>
                                          <Icon
                                            name="HiExternalLink"
                                            className="h-3.5 w-3.5 flex-shrink-0"
                                          />
                                        </a>
                                        {isPaid && (
                                          <Icon
                                            name="HiLockClosed"
                                            className="h-4 w-4 text-xtnd-dark-300 dark:text-xtnd-dark-400 flex-shrink-0"
                                            title="Link inactive - transaction is paid"
                                          />
                                        )}
                                      </div>
                                    );
                                  })()}
                                </td>
                              )}

                              {/* Operational Bill Section */}
                              <td className="px-6 py-4 text-sm font-medium text-blue-600 dark:text-blue-400 min-w-[120px]">
                                <div className="flex items-center space-x-2">
                                  <a
                                    href={`https://app.clio.com/bills/${invoice.operationalBill?.id || ''}`}
                                    target="clio-tab"
                                    rel="noopener noreferrer"
                                    className="hover:underline"
                                  >
                                    {invoice.operationalBill?.invoiceNumber || 'N/A'}
                                  </a>
                                  {invoice.operationalBill?.id && (
                                    <Button
                                      onClick={() =>
                                        copyToClipboard(
                                          invoice.operationalBill!.id.toString(),
                                          'Operational Bill ID'
                                        )
                                      }
                                      variant="ghost"
                                      size="sm"
                                      className="p-1 h-auto"
                                      title="Copy Operational Bill ID"
                                    >
                                      <Icon
                                        name="HiClipboard"
                                        className="h-3 w-3 text-xtnd-dark-400 hover:text-xtnd-dark-600 dark:text-xtnd-dark-300 dark:hover:text-xtnd-dark-100"
                                      />
                                    </Button>
                                  )}
                                </div>
                              </td>
                              {visibleColumns.operationalBillTotal && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[120px]">
                                  {invoice.operationalBill?.amount != null
                                    ? numberUtil.formatCurrency(invoice.operationalBill.amount)
                                    : '-'}
                                </td>
                              )}
                              {visibleColumns.operationalBillPaid && (
                                <td className="px-6 py-4 text-sm text-green-600 dark:text-green-400 min-w-[120px]">
                                  {invoice.operationalBill?.paid != null
                                    ? numberUtil.formatCurrency(invoice.operationalBill.paid)
                                    : '-'}
                                </td>
                              )}
                              {visibleColumns.operationalBillBalance && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[120px]">
                                  {invoice.operationalBill?.balance != null
                                    ? numberUtil.formatCurrency(invoice.operationalBill.balance)
                                    : '-'}
                                </td>
                              )}
                              {visibleColumns.operationalBillDue && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[120px]">
                                  {invoice.operationalBill?.due != null
                                    ? numberUtil.formatCurrency(invoice.operationalBill.due)
                                    : '-'}
                                </td>
                              )}
                              {visibleColumns.operationalBillIssuedAt && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[120px] responsive-datetime">
                                  {invoice.operationalBill?.issuedDate ? (
                                    <div className="flex flex-col sm:block">
                                      <div className="font-medium">
                                        {
                                          formatResponsiveDateTime(
                                            invoice.operationalBill.issuedDate
                                          ).date
                                        }
                                      </div>
                                      <div className="text-xs text-xtnd-dark-400 sm:hidden">
                                        {
                                          formatResponsiveDateTime(
                                            invoice.operationalBill.issuedDate
                                          ).time
                                        }
                                      </div>
                                    </div>
                                  ) : (
                                    ''
                                  )}
                                </td>
                              )}
                              {visibleColumns.operationalBillDueAt && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[120px] responsive-datetime">
                                  {invoice.operationalBill?.dueDate ? (
                                    <div className="flex flex-col sm:block">
                                      <div className="font-medium">
                                        {
                                          formatResponsiveDateTime(invoice.operationalBill.dueDate)
                                            .date
                                        }
                                      </div>
                                      <div className="text-xs text-xtnd-dark-400 sm:hidden">
                                        {
                                          formatResponsiveDateTime(invoice.operationalBill.dueDate)
                                            .time
                                        }
                                      </div>
                                    </div>
                                  ) : (
                                    ''
                                  )}
                                </td>
                              )}
                              {visibleColumns.lastOperationalPaymentAt && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[150px]">
                                  {invoice.lastOperationalPaymentAt ? (
                                    <div>
                                      <div className="font-medium">
                                        {formatResponsiveDateTime(invoice.lastOperationalPaymentAt).date}
                                      </div>
                                      {invoice.lastOperationalPaymentAmount !== undefined && (
                                        <div className="text-xs text-xtnd-dark-400">
                                          ${invoice.lastOperationalPaymentAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-xtnd-dark-300 dark:text-xtnd-dark-500">&mdash;</span>
                                  )}
                                </td>
                              )}
                              <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[200px] border-r-2 border-xtnd-dark-100 dark:border-xtnd-dark-500">
                                <span
                                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full text-center status-badge-mobile ${getTrustRequestStatusColor(
                                    formatRawStatus(invoice.operationalBill?.status)
                                  )}`}
                                >
                                  {formatRawStatus(invoice.operationalBill?.status)}
                                </span>
                              </td>

                              {/* Trust Request Bill Section */}
                              <td className="px-6 py-4 text-sm font-medium min-w-[120px]">
                                <div className="flex items-center space-x-2">
                                  {getTrustRequestNumber(invoice) &&
                                  getTrustRequestNumber(invoice) !== 'N/A' ? (
                                    <>
                                      <a
                                        href={`https://app.clio.com/bills/${invoice.trustRequest?.id || ''}`}
                                        target="clio-tab"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 dark:text-blue-400 hover:underline"
                                      >
                                        {getTrustRequestNumber(invoice)}
                                      </a>
                                      {invoice.trustRequest?.id && (
                                        <Button
                                          onClick={() =>
                                            copyToClipboard(
                                              invoice.trustRequest!.id.toString(),
                                              'Trust Request Bill ID'
                                            )
                                          }
                                          variant="ghost"
                                          size="sm"
                                          className="p-1 h-auto"
                                          title="Copy Trust Request Bill ID"
                                        >
                                          <Icon
                                            name="HiClipboard"
                                            className="h-3 w-3 text-xtnd-dark-400 hover:text-xtnd-dark-600 dark:text-xtnd-dark-300 dark:hover:text-xtnd-dark-100"
                                          />
                                        </Button>
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-xtnd-dark dark:text-xtnd-light">N/A</span>
                                  )}
                                </div>
                              </td>
                              {visibleColumns.trustRequestBillTotal && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[120px]">
                                  {invoice.trustRequest?.state === 'not_created'
                                    ? '-'
                                    : (invoice.trustRequest?.total != null
                                        ? numberUtil.formatCurrency(invoice.trustRequest.total)
                                        : '-')}
                                </td>
                              )}
                              {visibleColumns.trustRequestBillPaid && (
                                <td className="px-6 py-4 text-sm text-green-600 dark:text-green-400 min-w-[120px]">
                                  {invoice.trustRequest?.state === 'not_created'
                                    ? '-'
                                    : (invoice.trustRequest?.paid != null
                                        ? numberUtil.formatCurrency(invoice.trustRequest.paid)
                                        : '-')}
                                </td>
                              )}
                              {visibleColumns.trustRequestBillBalance && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[120px]">
                                  {invoice.trustRequest?.state === 'not_created'
                                    ? '-'
                                    : (invoice.trustRequest?.balance != null
                                        ? numberUtil.formatCurrency(invoice.trustRequest.balance)
                                        : '-')}
                                </td>
                              )}
                              {visibleColumns.trustRequestBillDue && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[120px]">
                                  {invoice.trustRequest?.state === 'not_created'
                                    ? '-'
                                    : (invoice.trustRequest?.due != null
                                        ? numberUtil.formatCurrency(invoice.trustRequest.due)
                                        : '-')}
                                </td>
                              )}
                              {visibleColumns.trustRequestBillPending && (
                                <td className="px-6 py-4 text-sm text-yellow-600 dark:text-yellow-400 min-w-[120px]">
                                  {invoice.trustRequest?.state === 'not_created'
                                    ? '-'
                                    : (invoice.trustRequest?.pending != null
                                        ? numberUtil.formatCurrency(invoice.trustRequest.pending)
                                        : '-')}
                                </td>
                              )}
                              {visibleColumns.trustRequestBillIssuedAt && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[120px] responsive-datetime">
                                  {invoice.trustRequest?.issued_at ? (
                                    <div className="flex flex-col sm:block">
                                      <div className="font-medium">
                                        {
                                          formatResponsiveDateTime(invoice.trustRequest.issued_at)
                                            .date
                                        }
                                      </div>
                                      <div className="text-xs text-xtnd-dark-400 sm:hidden">
                                        {
                                          formatResponsiveDateTime(invoice.trustRequest.issued_at)
                                            .time
                                        }
                                      </div>
                                    </div>
                                  ) : (
                                    ''
                                  )}
                                </td>
                              )}
                              {visibleColumns.trustRequestBillDueAt && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[120px] responsive-datetime">
                                  {invoice.trustRequest?.due_at ? (
                                    <div className="flex flex-col sm:block">
                                      <div className="font-medium">
                                        {formatResponsiveDateTime(invoice.trustRequest.due_at).date}
                                      </div>
                                      <div className="text-xs text-xtnd-dark-400 sm:hidden">
                                        {formatResponsiveDateTime(invoice.trustRequest.due_at).time}
                                      </div>
                                    </div>
                                  ) : (
                                    ''
                                  )}
                                </td>
                              )}
                              {visibleColumns.lastTrustRequestPaymentAt && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[150px]">
                                  {invoice.lastTrustRequestPaymentAt ? (
                                    <div>
                                      <div className="font-medium">
                                        {formatResponsiveDateTime(invoice.lastTrustRequestPaymentAt).date}
                                      </div>
                                      {invoice.lastTrustRequestPaymentAmount !== undefined && (
                                        <div className="text-xs text-xtnd-dark-400">
                                          ${invoice.lastTrustRequestPaymentAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-xtnd-dark-300 dark:text-xtnd-dark-500">&mdash;</span>
                                  )}
                                </td>
                              )}
                              {visibleColumns.trustRequestEmailType && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[160px]">
                                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-xtnd-light dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-light">
                                    {formatEmailType(invoice.trustRequestEmailType)}
                                  </span>
                                </td>
                              )}
                              <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[200px]">
                                <span
                                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full text-center status-badge-mobile ${getTrustRequestStatusColor(
                                    formatRawStatus(invoice.trustRequest?.trrStatus)
                                  )}`}
                                >
                                  {formatRawStatus(invoice.trustRequest?.trrStatus)}
                                </span>
                              </td>

                              {/* Trust Request Calculation */}
                              {visibleColumns.trustRequestCalculation && (
                                <td className="px-6 py-4 text-sm text-xtnd-dark dark:text-xtnd-light min-w-[240px] border-r-2 border-xtnd-dark-100 dark:border-xtnd-dark-500">
                                  <span className="font-mono text-xs whitespace-nowrap">
                                    {formatTrustRequestCalculation(invoice)}
                                  </span>
                                </td>
                              )}

                              {/* Actions - Dropdown Menu - Sticky Right */}
                              <td
                                className={`py-4 px-3 text-lg sticky right-0 shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.1)] transition-colors ${
                                  isSelected
                                    ? 'bg-teal-50 dark:bg-teal-900'
                                    : isActive
                                      ? 'bg-xtnd-dark-100 dark:bg-xtnd-dark-600'
                                      : 'bg-white dark:bg-xtnd-dark-700 group-hover:bg-xtnd-dark-100 dark:group-hover:bg-xtnd-dark-600'
                                } ${
                                  actionsMenuOpen === invoice.id ? 'z-50' : 'z-10'
                                }`}
                                onClick={e => e.stopPropagation()}
                              >
                                <div className="flex items-center justify-center space-x-2">
                                  {reprocessing.has(invoice.id) ? (
                                    <Icon
                                      name="HiRefresh"
                                      className="h-5 w-5 animate-spin text-xtnd-blue"
                                    />
                                  ) : (
                                    <button
                                      onClick={e => {
                                        const newState =
                                          actionsMenuOpen === invoice.id ? null : invoice.id;
                                        setActionsMenuOpen(newState);
                                        setMenuTriggerElement(newState ? e.currentTarget : null);
                                      }}
                                      className="p-1.5 hover:bg-xtnd-dark-50 dark:hover:bg-xtnd-dark-600 rounded-lg transition-colors"
                                      title="More actions"
                                    >
                                      <Icon
                                        name="HiDotsVertical"
                                        className="h-5 w-5 text-xtnd-dark-400 dark:text-xtnd-dark-300"
                                      />
                                    </button>
                                  )}

                                  {/* Dropdown Menu */}
                                  <ActionsMenu
                                    isOpen={actionsMenuOpen === invoice.id}
                                    onClose={() => setActionsMenuOpen(null)}
                                    triggerElement={menuTriggerElement}
                                  >
                                    {/* View in Clio */}
                                    <ActionsMenuItem
                                      icon="HiFolder"
                                      iconColor="text-xtnd-blue"
                                      label="View in Clio"
                                      onClick={() => {
                                        const matterId = invoice.matterId;
                                        if (matterId && matterId !== 'Unknown') {
                                          window.open(
                                            `https://app.clio.com/nc/#/matters/${matterId}/documents`,
                                            'clio-tab'
                                          );
                                        } else {
                                          showToast.error('Matter ID not available');
                                        }
                                        setActionsMenuOpen(null);
                                      }}
                                    />

                                    {/* View PDF */}
                                    {(() => {
                                      const hasPdf = invoice.pdfs?.invoice;
                                      const isValidStatus =
                                        invoice.juniperStatus
                                          ?.toLowerCase()
                                          .includes('email held') ||
                                        invoice.juniperStatus?.toLowerCase().includes('completed');
                                      const canView = hasPdf && isValidStatus;

                                      return (
                                        <ActionsMenuItem
                                          icon="HiDownload"
                                          iconColor={canView ? 'text-green-600' : 'text-xtnd-dark-300'}
                                          label="View/Download Invoice"
                                          disabled={!canView}
                                          title={
                                            !canView
                                              ? `PDF not available - Transaction must be 'email-held' or 'completed' and have PDF`
                                              : ''
                                          }
                                          onClick={() => {
                                            if (canView) {
                                              handleQuickViewPdf(invoice);
                                              setActionsMenuOpen(null);
                                            }
                                          }}
                                        />
                                      );
                                    })()}

                                    {/* Download Receipts ZIP */}
                                    {(() => {
                                      const receiptCount =
                                        invoice.documents?.storedAssets?.receipts?.length || 0;
                                      return receiptCount > 0 ? (
                                        <ActionsMenuItem
                                          icon="HiDownload"
                                          iconColor="text-green-500"
                                          label={`Download ${receiptCount} Receipt${receiptCount > 1 ? 's' : ''} (ZIP)`}
                                          onClick={() => {
                                            handleDownloadReceiptsZip(invoice);
                                            setActionsMenuOpen(null);
                                          }}
                                        />
                                      ) : null;
                                    })()}

                                    <ActionsMenuDivider />

                                    {/* Send Email */}
                                    <ActionsMenuItem
                                      icon="HiMail"
                                      iconColor={
                                        invoice.juniperStatus?.toLowerCase().includes('email held')
                                          ? 'text-yellow-500'
                                          : invoice.juniperStatus
                                                ?.toLowerCase()
                                                .includes('completed')
                                            ? 'text-green-500'
                                            : 'text-xtnd-dark-300'
                                      }
                                      label={
                                        invoice.juniperStatus?.toLowerCase() === 'completed'
                                          ? 'Send Payment Reminder'
                                          : 'Send Email'
                                      }
                                      disabled={
                                        resendEmail.isPending ||
                                        invoice.emailQueued ||
                                        !isResendEligible(invoice) ||
                                        !hasValidRecipients(invoice) ||
                                        processingLocked
                                      }
                                      title={
                                        processingLocked
                                          ? 'Locked — bulk action in flight'
                                          : !isResendEligible(invoice)
                                            ? 'Only available for Trust Request bills in awaiting_payment or paid status, Email Held bills, or Completed transactions'
                                            : !hasValidRecipients(invoice)
                                              ? 'No valid email recipients'
                                              : 'Send email or payment reminder'
                                      }
                                      onClick={() => {
                                        openConfirmForTransaction(invoice);
                                        setActionsMenuOpen(null);
                                      }}
                                    />

                                    {/* Mark Completed */}
                                    {isMarkCompletedEligible(invoice) && (
                                      <ActionsMenuItem
                                        icon="HiCheckCircle"
                                        iconColor="text-green-500"
                                        label="Mark Completed"
                                        disabled={markCompleted.isPending || processingLocked}
                                        title={processingLocked ? 'Locked — bulk action in flight' : 'Mark as completed without sending email'}
                                        onClick={() => {
                                          setActionsMenuOpen(null);
                                          markCompleted.mutate({ juniperId: invoice.juniperId || invoice.id });
                                        }}
                                      />
                                    )}

                                    {/* View Bank Transactions */}
                                    <ActionsMenuItem
                                      icon="HiCreditCard"
                                      iconColor="text-xtnd-blue"
                                      label="View Bank Transactions"
                                      onClick={() => {
                                        setSelectedTransactionForBankModal(invoice);
                                        setBankTransactionsModalOpen(true);
                                        setActionsMenuOpen(null);
                                      }}
                                      disabled={!invoice.matterId || invoice.matterId === 'Unknown'}
                                      title={
                                        !invoice.matterId || invoice.matterId === 'Unknown'
                                          ? 'Matter ID not available'
                                          : 'View bank transactions for this matter'
                                      }
                                    />

                                    <ActionsMenuDivider />

                                    {/* Regenerate PDF - Grouped with refresh actions */}
                                    <ActionsMenuItem
                                      icon="HiRefresh"
                                      iconColor={
                                        isPdfRegenerationEligible(invoice)
                                          ? 'text-orange-500'
                                          : 'text-xtnd-dark-300'
                                      }
                                      label="Regenerate PDF"
                                      disabled={
                                        regeneratePdf.isPending ||
                                        !isPdfRegenerationEligible(invoice) ||
                                        processingLocked
                                      }
                                      title={
                                        processingLocked
                                          ? 'Locked — bulk action in flight'
                                          : !isPdfRegenerationEligible(invoice)
                                            ? 'Only available for Evergreen bills in Awaiting Payment or Partial Payment, or Operational bills in Email Held / Completed (when the bill is not Paid, Voided, or Deleted)'
                                            : 'Generate fresh PDF with new Clio Payment link'
                                      }
                                      onClick={async () => {
                                        if (isPdfRegenerationEligible(invoice)) {
                                          setActionsMenuOpen(null);
                                          openRegenerateConfirm('single', invoice);
                                        }
                                      }}
                                    />

                                    {/* Refresh from Clio */}
                                    <ActionsMenuItem
                                      icon="HiRefresh"
                                      iconColor="text-xtnd-blue"
                                      label="Refresh from Clio"
                                      disabled={reprocessing.has(invoice.id) || processingLocked}
                                      title={processingLocked ? 'Locked — bulk action in flight' : undefined}
                                      onClick={async () => {
                                        const id = invoice.id;
                                        setActionsMenuOpen(null);
                                        setReprocessing(prev => new Set(prev).add(id));
                                        try {
                                          showToast.success('Refreshing transaction data...');
                                          const requestId = `refresh-${id}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
                                          await api.post(`/juniper/transactions/${id}/reprocess`, {
                                            requestId,
                                            requestType: 'refresh',
                                            reason: 'Manual refresh from juniper transactions page',
                                          });
                                          showToast.success(
                                            'Refresh initiated - check back in a few moments...'
                                          );
                                        } catch (e: any) {
                                          showToast.error(
                                            `Refresh failed: ${e?.message || 'Unknown error'}`
                                          );
                                        } finally {
                                          setReprocessing(prev => {
                                            const next = new Set(prev);
                                            next.delete(id);
                                            return next;
                                          });
                                        }
                                      }}
                                    />

                                    {isVoidDeleteEligible(invoice) && (
                                      <>
                                        <ActionsMenuDivider />
                                        <ActionsMenuItem
                                          icon="HiTrash"
                                          iconColor="text-red-500"
                                          label={`${getVoidDeleteAction(invoice)} Trust Request`}
                                          disabled={bulkVoidDelete.isPending || processingLocked}
                                          title={processingLocked ? 'Locked — bulk action in flight' : `${getVoidDeleteAction(invoice)} the trust request bill in Clio`}
                                          onClick={() => {
                                            setActionsMenuOpen(null);
                                            handleSingleVoidDelete(invoice);
                                          }}
                                        />
                                      </>
                                    )}
                                  </ActionsMenu>
                                </div>
                              </td>
                            </tr>
                            );
                          })}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>

          {/* Right-side Utility Rail + Drawer */}
          <TransactionDrawer
            activeRow={activeRow}
            isOpen={isDrawerOpen}
            activeTab={activeDrawerTab}
            onTabChange={handleDrawerTabChange}
            onClose={handleDrawerClose}
            tenantId={tenantId}
            formatRawStatus={formatRawStatus}
            formatCurrency={numberUtil.formatCurrency}
            formatDateTime={formatResponsiveDateTime}
            getTransactionWarnings={getTransactionWarnings}
            deliveryDetails={deliveryDetails}
            isLoadingDeliveryDetails={isLoadingDeliveryDetails || isFetchingDeliveryDetails}
            documentHistoryRows={documentHistoryResponse?.rows || []}
            isLoadingDocumentHistory={isLoadingDocumentHistory || isFetchingDocumentHistory}
            actions={{
              onViewInClio: (invoice) => {
                const matterId = invoice.matterId;
                if (matterId && matterId !== 'Unknown') {
                  window.open(`https://app.clio.com/nc/#/matters/${matterId}/documents`, 'clio-tab');
                } else {
                  showToast.error('Matter ID not available');
                }
              },
              onViewPdf: (invoice) => handleQuickViewPdf(invoice),
              onDownloadReceipts: (invoice) => handleDownloadReceiptsZip(invoice),
              onSendEmail: (invoice) => {
                openConfirmForTransaction(invoice);
              },
              onMarkCompleted: (invoice) => {
                markCompleted.mutate({ juniperId: invoice.juniperId || invoice.id });
              },
              onViewBankTransactions: (invoice) => {
                setSelectedTransactionForBankModal(invoice);
                setBankTransactionsModalOpen(true);
              },
              onRegeneratePdf: (invoice) => {
                if (isPdfRegenerationEligible(invoice)) {
                  openRegenerateConfirm('single', invoice);
                }
              },
              onRefreshFromClio: async (invoice) => {
                const id = invoice.id;
                setReprocessing(prev => new Set(prev).add(id));
                try {
                  showToast.success('Refreshing transaction data...');
                  const requestId = `refresh-${id}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
                  await api.post(`/juniper/transactions/${id}/reprocess`, {
                    requestId,
                    requestType: 'refresh',
                    reason: 'Manual refresh from juniper transactions page',
                  });
                  showToast.success('Refresh initiated - check back in a few moments...');
                } catch (e: any) {
                  showToast.error(`Refresh failed: ${e?.message || 'Unknown error'}`);
                } finally {
                  setReprocessing(prev => {
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                  });
                }
              },
              onVoidDeleteTrustRequest: (invoice) => handleSingleVoidDelete(invoice),
              isResendEligible,
              hasValidRecipients,
              isPdfRegenerationEligible,
              isMarkCompletedEligible,
              isVoidDeleteEligible,
              getVoidDeleteAction,
              isReprocessing: activeRow ? reprocessing.has(activeRow.id) : false,
              isResendPending: resendEmail.isPending,
              isRegeneratePending: regeneratePdf.isPending,
              isMarkCompletedPending: markCompleted.isPending,
              isVoidDeletePending: bulkVoidDelete.isPending,
            }}
          />
        </div>


        {/* Pagination */}
        {paginationComponent}

        {/* Bulk Simulate Modal */}
        <BulkSimulateModal
          isOpen={bulkSimulateModalOpen}
          onClose={() => setBulkSimulateModalOpen(false)}
          onSubmit={handleBulkSimulate}
          selectedCount={selection.selectedCount}
          isLoading={bulkSimulate.isPending}
        />

        {/* Bank Transactions Modal */}
        <BankTransactionsModal
          isOpen={bankTransactionsModalOpen}
          onClose={() => {
            setBankTransactionsModalOpen(false);
            setSelectedTransactionForBankModal(null);
          }}
          matterId={selectedTransactionForBankModal?.matterId || null}
          matterName={selectedTransactionForBankModal?.matterName}
        />

        {/* Download Menu */}
        <DownloadMenu
          transaction={downloadMenuState.transaction}
          isOpen={downloadMenuState.isOpen}
          onClose={() => setDownloadMenuState(prev => ({ ...prev, isOpen: false }))}
          onViewPdf={handleViewPdf}
          onViewReceipt={handleViewReceipt}
          onDownloadReceiptsZip={() => handleDownloadReceiptsZip(downloadMenuState.transaction)}
          position={downloadMenuState.position}
        />

      {/* Download Menu */}
      </>
      ) : (
        <TrustRequestsTransactionsView />
      )}

      {/* Shared Modals (shown for both tabs) */}
      {/* Webhook Simulation Modal */}
      <WebhookSimulationModal
        isOpen={isWebhookModalOpen}
        onClose={() => setIsWebhookModalOpen(false)}
        onSuccess={() => {
          // Optionally refresh data after successful webhook simulation
        }}
      />

      <DownloadMenu
        transaction={downloadMenuState.transaction}
        isOpen={downloadMenuState.isOpen}
        onClose={() => setDownloadMenuState(prev => ({ ...prev, isOpen: false }))}
        onViewPdf={handleViewPdf}
        onViewReceipt={handleViewReceipt}
        onDownloadReceiptsZip={() => handleDownloadReceiptsZip(downloadMenuState.transaction)}
        position={downloadMenuState.position}
      />

      {/* Email Send Confirmation Modal */}
      <EmailSendConfirmationModal
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setConfirmTarget(null);
          setBulkEligibleIds([]);
          setBulkEligibleByType({});
          setBulkHeldCountByType({});
          previewRecipients.reset();
        }}
        onConfirm={(emailTemplateOverrideId, bulkOverrides) => {
          if (confirmTarget?._bulk) {
            const types = Object.keys(bulkEligibleByType);
            if (bulkOverrides && types.length > 1) {
              for (const type of types) {
                const ids = bulkEligibleByType[type];
                if (ids?.length) {
                  bulkSendEmails.mutate(
                    { juniperIds: ids, emailTemplateOverrideId: bulkOverrides[type] },
                    { onSuccess: trackBulkJob },
                  );
                }
              }
            } else {
              bulkSendEmails.mutate(
                { juniperIds: bulkEligibleIds, emailTemplateOverrideId },
                { onSuccess: trackBulkJob },
              );
            }
            setConfirmOpen(false);
            setConfirmTarget(null);
            setBulkEligibleIds([]);
            setBulkEligibleByType({});
            setBulkHeldCountByType({});
            previewRecipients.reset();
          } else if (confirmTarget?.id) {
            const isEmailHeld = confirmTarget.juniperStatus?.toLowerCase() === 'email held';
            const mutation = isEmailHeld ? releaseEmail : resendEmail;
            mutation.mutate({
              juniperId: confirmTarget.id,
              tenantId: confirmTarget.tenantId || 'current',
              emailTemplateOverrideId,
            });
            setConfirmOpen(false);
            setConfirmTarget(null);
          }
        }}
        title={
          confirmTarget?._bulk
            ? `Send ${bulkEligibleIds.length} Email${bulkEligibleIds.length === 1 ? '' : 's'}`
            : `Confirm Email ${(confirmTarget?.juniperStatus?.toLowerCase() === 'email held' || confirmTarget?.juniperStatus?.toLowerCase() === 'completed') ? 'Resend' : 'Send'}`
        }
        confirmLabel={
          confirmTarget?._bulk
            ? `Send ${bulkEligibleIds.length} Email${bulkEligibleIds.length === 1 ? '' : 's'}`
            : (confirmTarget?.juniperStatus?.toLowerCase() === 'email held' || confirmTarget?.juniperStatus?.toLowerCase() === 'completed') ? 'Send Payment Reminder' : 'Send Email'
        }
        isPending={resendEmail.isPending || releaseEmail.isPending || bulkSendEmails.isPending}
        isLoading={previewRecipients.isLoading}
        toRecipients={previewRecipients.toRecipients}
        bccRecipients={previewRecipients.bccRecipients}
        actualRecipients={previewRecipients.actualRecipients}
        overrideActive={previewRecipients.overrideActive}
        billType={
          confirmTarget?._bulk
            ? (Object.keys(bulkEligibleByType)[0] as 'operational' | 'evergreen' | 'trust') || 'operational'
            : confirmTarget?.matterType === 'Evergreen'
              ? 'evergreen'
              : 'operational'
        }
        isReminder={
          confirmTarget?._bulk
            ? Object.values(bulkHeldCountByType).reduce((sum, n) => sum + n, 0) === 0
            : confirmTarget?.juniperStatus?.toLowerCase() !== 'email held'
        }
        bulkCount={confirmTarget?._bulk ? bulkEligibleIds.length : undefined}
        bulkMatterTypes={confirmTarget?._bulk
          ? Object.entries(bulkEligibleByType).map(([type, ids]) => ({
              type: type as 'operational' | 'evergreen' | 'trust',
              count: ids.length,
              isReminder: (bulkHeldCountByType[type] || 0) === 0,
            }))
          : undefined
        }
        onRefreshContacts={confirmTarget && !confirmTarget._bulk ? previewRecipients.refresh : undefined}
        isRefreshingContacts={previewRecipients.isRefreshing}
      />

        {/* PDF Regeneration Confirmation Modal */}
        <Dialog open={regenerateConfirmOpen} onClose={() => setRegenerateConfirmOpen(false)} zIndex="z-[300]">
          <DialogHeader>Confirm PDF Regeneration</DialogHeader>
          <DialogBody>
            <div className="space-y-3 text-sm text-xtnd-dark">
              {regenerateConfirmType === 'single' && regenerateConfirmTarget ? (
                <div>
                  <p className="font-medium">
                    Are you sure you want to regenerate the PDF for this transaction?
                  </p>
                  <div className="mt-3 bg-xtnd-light-400 rounded p-3 space-y-2">
                    <div>
                      <strong>Client:</strong> {regenerateConfirmTarget.client}
                    </div>
                    <div>
                      <strong>Matter:</strong> {regenerateConfirmTarget.matterName}
                    </div>
                    <div>
                      <strong>Matter Type:</strong> {regenerateConfirmTarget.matterType || 'Unknown'}
                    </div>
                    <div>
                      <strong>Amount:</strong>{' '}
                      {numberUtil.formatCurrency(
                        regenerateConfirmTarget.matterType === 'Operational'
                          ? regenerateConfirmTarget.operationalBill?.amount || 0
                          : regenerateConfirmTarget.trustRequest?.total ||
                            regenerateConfirmTarget.trustRequest?.amount ||
                            0
                      )}
                    </div>
                    <div>
                      <strong>Status:</strong>{' '}
                      {regenerateConfirmTarget.matterType === 'Operational'
                        ? formatRawStatus(regenerateConfirmTarget.operationalBill?.state)
                        : formatRawStatus(regenerateConfirmTarget.trustRequest?.state)}
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-xtnd-dark-500">
                    This will generate a fresh PDF with a new Clio Payment link. The existing PDF
                    will be replaced.
                  </p>
                </div>
              ) : regenerateConfirmType === 'bulk' && regenerateConfirmBulkIds.length > 0 ? (
                <div>
                  <p className="font-medium">
                    Are you sure you want to regenerate PDFs for {regenerateConfirmBulkIds.length}{' '}
                    transaction{regenerateConfirmBulkIds.length > 1 ? 's' : ''}?
                  </p>
                  <div className="mt-3 bg-xtnd-light-400 rounded p-3">
                    <p className="text-xs text-xtnd-dark-500">
                      This will generate fresh PDFs with new Clio Payment links for all eligible
                      transactions. Only Trust Request bills in "Awaiting Payment" status and
                      Operational transactions in "Email Held" or "Completed" status (where the
                      operational bill is not Paid, Voided, or Deleted) will be regenerated.
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRegenerateConfirmOpen(false)}
              disabled={regeneratePdf.isPending || bulkRegeneratePdf.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleRegenerateConfirm}
              disabled={regeneratePdf.isPending || bulkRegeneratePdf.isPending}
              loading={regeneratePdf.isPending || bulkRegeneratePdf.isPending}
            >
              {regeneratePdf.isPending || bulkRegeneratePdf.isPending
                ? 'Regenerating...'
                : 'Regenerate PDF'}
            </Button>
          </DialogFooter>
        </Dialog>

        {/* Void/Delete Trust Request Confirmation Dialog */}
        <Dialog open={voidDeleteConfirmOpen} onClose={() => setVoidDeleteConfirmOpen(false)} zIndex="z-[300]">
          <DialogHeader>
            {voidDeleteIsBulk
              ? 'Confirm Void/Delete Trust Requests'
              : `Confirm ${(voidDeleteTarget && getVoidDeleteAction(voidDeleteTarget)) || 'Delete'} Trust Request`}
          </DialogHeader>
          <DialogBody>
            <div className="space-y-3 text-sm text-xtnd-dark">
              {!voidDeleteIsBulk && voidDeleteTarget ? (
                <div>
                  <p className="font-medium">
                    Are you sure you want to {getVoidDeleteAction(voidDeleteTarget)?.toLowerCase()} this trust request?
                  </p>
                  <div className="mt-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 space-y-2">
                    <div>
                      <strong>Client:</strong> {voidDeleteTarget.client}
                    </div>
                    <div>
                      <strong>Matter:</strong> {voidDeleteTarget.matterName}
                    </div>
                    <div>
                      <strong>Trust Request State:</strong>{' '}
                      <span className="capitalize">{voidDeleteTarget.trustRequest?.state?.replace(/_/g, ' ')}</span>
                    </div>
                    <div>
                      <strong>Amount:</strong>{' '}
                      {numberUtil.formatCurrency(
                        voidDeleteTarget.trustRequest?.total ||
                        voidDeleteTarget.trustRequest?.amount ||
                        0
                      )}
                    </div>
                  </div>
                  <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
                    <p className="text-xs text-red-700 dark:text-red-300 font-medium">
                      This action cannot be undone. The trust request bill will be {getVoidDeleteAction(voidDeleteTarget)?.toLowerCase()}d in Clio.
                    </p>
                  </div>
                </div>
              ) : voidDeleteIsBulk ? (
                (() => {
                  // Bulk void/delete is only available in 'specific' mode, so we have full
                  // objects for every selected row (including across pages).
                  const eligible = selection.getSpecificObjects().filter(isVoidDeleteEligible);
                  const bulkDeleteCount = eligible.filter(inv => {
                    const s = inv.trustRequest?.state?.toLowerCase();
                    return s === 'draft' || s === 'awaiting_approval';
                  }).length;
                  const bulkVoidCount = eligible.filter(inv =>
                    inv.trustRequest?.state?.toLowerCase() === 'awaiting_payment'
                  ).length;
                  return (
                    <div>
                      <p className="font-medium">
                        Are you sure you want to void/delete {eligible.length} trust request{eligible.length > 1 ? 's' : ''}?
                      </p>
                      <div className="mt-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 space-y-1">
                        {bulkDeleteCount > 0 && (
                          <p className="text-sm">
                            <strong>{bulkDeleteCount}</strong> trust request{bulkDeleteCount > 1 ? 's' : ''} will be <strong>deleted</strong> (draft/awaiting approval)
                          </p>
                        )}
                        {bulkVoidCount > 0 && (
                          <p className="text-sm">
                            <strong>{bulkVoidCount}</strong> trust request{bulkVoidCount > 1 ? 's' : ''} will be <strong>voided</strong> (awaiting payment)
                          </p>
                        )}
                      </div>
                      <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
                        <p className="text-xs text-red-700 dark:text-red-300 font-medium">
                          This action cannot be undone. The trust request bills will be voided/deleted in Clio.
                        </p>
                      </div>
                    </div>
                  );
                })()
              ) : null}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setVoidDeleteConfirmOpen(false)}
              disabled={bulkVoidDelete.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={confirmVoidDelete}
              disabled={bulkVoidDelete.isPending}
              loading={bulkVoidDelete.isPending}
              className="bg-red-600 hover:bg-red-700 border-red-600 hover:border-red-700 text-white"
            >
              {bulkVoidDelete.isPending
                ? 'Processing...'
                : voidDeleteIsBulk
                  ? 'Confirm Void/Delete'
                  : `${(voidDeleteTarget && getVoidDeleteAction(voidDeleteTarget)) || 'Delete'} Trust Request`}
            </Button>
          </DialogFooter>
        </Dialog>
    </PageLayout>
  );
}
